import axios, { CanceledError } from 'axios';
import isJSON from 'is-json';
import jwtDecode from 'jwt-decode';
import { camelCase, get, identity, pickBy, set } from 'lodash';
import querystring from 'querystring';

import { redirectToUrl } from '@ezbobdev/ui-framework';

import { EXCHANGE_TOKEN, ONE_TIME_PASSWORD } from './constants';

const axiosInstance = axios.create();

/**
 * Checks if the current runtime environment is Node.js.
 *
 * This function examines the global `process` object, which is specific to Node.js environments,
 * to determine if the code is running in Node.js. It checks if `process` is an object, then further
 * checks if `process.versions` is an object, and finally checks if `process.versions.node` is defined.
 * If all these checks pass, it concludes that the environment is Node.js and returns true; otherwise, it returns false.
 *
 * @returns {boolean} True if the current runtime environment is Node.js, false otherwise.
 */
function isNode() {
  let isNode = false;
  if (typeof process === 'object') {
    if (typeof process.versions === 'object') {
      if (typeof process.versions.node !== 'undefined') {
        isNode = true;
      }
    }
  }

  return isNode;
}

/**
 * Configures Axios request interceptors to handle authorization and custom configurations.
 *
 * This interceptor function is designed to modify the Axios request configuration before the request is sent.
 * It performs several tasks based on the SDK's configuration:
 *
 * - If the SDK is in demo mode (`Tools.options.demoMode` is true), it bypasses the interceptor logic by returning early.
 * - Sets default values for `disallowHeaders` and `tokenScope` in the request configuration if they are not already defined.
 *   - `disallowHeaders` is an array that specifies which headers should not be included in the request. By default, it's an empty array.
 *   - `tokenScope` determines which type of token (e.g., `accessToken`, `appToken`) should be included in the Authorization header. The default is `accessToken`.
 * - If the authentication is not done via cookies and the `Authorization` header is not disallowed (`disallowHeaders` does not include 'Authorization'), it constructs the `Authorization` header using the specified `tokenType` and token. The token is determined based on the `tokenScope` and fallbacks to `caseToken` or `appToken` if necessary.
 * - If a `requestCallback` function is defined in `Tools.options`, it calls this function with the current request configuration. This allows for custom modifications to the request configuration. The modified configuration is then returned.
 *
 * If an error occurs during request configuration, the interceptor rejects the promise with the encountered error.
 *
 * @param {function} config - The Axios request configuration object that can be modified before the request is sent.
 * @returns {function|Promise} The modified Axios request configuration object or a Promise that rejects with an error.
 */
axiosInstance.interceptors.request.use(
  function (config) {
    if (Tools.options.demoMode) return;

    config.disallowHeaders = config.disallowHeaders || [];

    if (Tools.getAuthConfig('withCredentials')) {
      config.withCredentials = true;
      config.headers['X-CSRF-Token'] = Tools.getOption('csrfToken');

      const productPath = Tools.getAuthConfig('productPath');
      if (productPath) {
        config.headers['X-Product-Path'] = productPath;
      }
    } else if (!config.disallowHeaders.includes('Authorization')) {
      const tokenType = config.tokenType || Tools.getAuthConfig('tokenType');

      config.tokenScope = config.tokenScope || 'accessToken'; // what token to send (accessToken, appToken). Defaults: accessToken (user token)
      const token =
        config.token || Tools.options[config.tokenScope] || Tools.options['caseToken'] || Tools.options['appToken'];

      config.headers.Authorization = `${tokenType} ${token}`;
    }

    const recaptcha3Token = Tools.getOption('recaptcha3Token');
    const recaptcha2Token = Tools.getOption('recaptcha2Token');

    if (recaptcha3Token) {
      config.headers['recaptchaV3-key'] = Tools.getOption('recaptcha3Sitekey');
      config.headers['recaptchaV3-token'] = recaptcha3Token;

      Tools.removeOption('recaptcha3Token');
    }

    if (recaptcha2Token) {
      config.headers['recaptchaV2-key'] = Tools.getOption('recaptcha2Sitekey');
      config.headers['recaptchaV2-token'] = recaptcha2Token;
    }

    const caseUuid = Tools.getOption('caseId');
    if (caseUuid) {
      config.headers['case_uuid'] = caseUuid;
    }

    if (typeof Tools.options.requestCallback === 'function') {
      config = Tools.options.requestCallback(config);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Single-flight guards for token renewal (GRN-6822).
 *
 * Concurrent 401s must trigger exactly ONE renewal. The backend rotates the
 * refresh-token family on every use and runs reuse detection, so firing two
 * refreshes in parallel would present a just-rotated (now-invalid) token on the
 * second call and trip reuse detection — killing the whole session. Every caller
 * awaits the same in-flight promise instead.
 */
let userSessionRefresh = null;
let appTokenRemint = null;

/**
 * Refreshes the user session access token via the `refresh_token` grant.
 *
 * In cookie mode the refresh token is an HttpOnly cookie (Path=/v1/oauth2/token)
 * that JS cannot read — it is sent implicitly with credentials, so no value is
 * passed. In bearer mode the stored refresh token is sent explicitly. On success
 * the (rotated) token response is mapped back into `Tools.options`.
 *
 * @returns {Promise} The in-flight (de-duplicated) refresh promise.
 */
function refreshUserSession() {
  if (!userSessionRefresh) {
    const refreshTokenValue = Tools.getAuthConfig('withCredentials')
      ? undefined
      : Tools.getOption('refreshToken');

    userSessionRefresh = Oauth.login(undefined, undefined, 'refresh_token', refreshTokenValue)
      .then((loginResults) => {
        for (let property in loginResults.data) {
          Tools.setOption(camelCase(property), loginResults.data[property]);
        }
        return loginResults;
      })
      .finally(() => {
        userSessionRefresh = null;
      });
  }

  return userSessionRefresh;
}

/**
 * Re-mints the anonymous app (guest) token.
 *
 * Guest tokens have no refresh token by design, so an expired guest token is
 * renewed by requesting a fresh app token (the configured guest grant, e.g.
 * `client_proxy`), preserving any live session cookies. The new CSRF token and
 * app token are mapped back into `Tools.options` for the retried request.
 *
 * @returns {Promise} The in-flight (de-duplicated) re-mint promise.
 */
function remintAppToken() {
  if (!appTokenRemint) {
    appTokenRemint = Oauth.getAppToken(Tools.getOption('channelId'), true)
      .then((tokenResults) => {
        const { access_token, token_type, csrfToken } = tokenResults.data;
        Tools.setOption('csrfToken', csrfToken);
        Tools.setOption('appToken', access_token);
        Tools.setAuthConfig({ tokenType: token_type });
        return tokenResults;
      })
      .finally(() => {
        appTokenRemint = null;
      });
  }

  return appTokenRemint;
}

/**
 * Configures Axios response interceptors to handle responses and errors globally.
 *
 * The success interceptor is triggered on a successful response from the server. It performs the following actions:
 * - If `response.config.responseName` is set, it stores the response using `Tools.setResponse` for later retrieval.
 * - If a `responseCallback` function is defined in `Tools.options`, it calls this function with the current response, allowing for custom response handling. The potentially modified response is then returned.
 *
 * The error interceptor is triggered on a response error. It handles specific error scenarios, particularly token-related errors:
 * - If a `responseCallback` function is defined, it calls this function with the error, allowing for custom error handling.
 * - If the error is due to an 'invalid_token', it attempts to refresh the token using the refresh token workflow. If successful, it retries the original request with the new access token.
 * - If the token cannot be refreshed (e.g., no refresh token available, or the refresh token is invalid), it logs out the user and redirects to a logout URL.
 * - For errors not related to 'invalid_token', it simply rejects the promise with the encountered error.
 *
 * This interceptor setup allows for centralized response and error handling across all Axios requests made using the `axiosInstance`.
 *
 * @param {function} response - The function to handle successful responses. Modifies the response or performs actions based on it.
 * @param {function} error - The async function to handle errors. It can modify the error, perform retries for specific cases, or handle token refresh logic.
 * @returns {Promise} The original or modified response for success interceptor, or a Promise rejection for error interceptor.
 */
axiosInstance.interceptors.response.use(
  /**
   * Handles successful Axios responses.
   *
   * This function is designed to be used as a response interceptor for Axios requests. It performs two main actions:
   *
   * 1. If the `response.config` object contains a `responseName` property, it uses this name to store the response
   *    using `Tools.setResponse`. This allows for easy retrieval of the response elsewhere in the application.
   *
   * 2. Checks if `Tools.options` has a `responseCallback` function defined. If so, it calls this function with the
   *    current response object, allowing for custom processing or modification of the response. The function can
   *    modify the response object, and the modified response is then returned.
   *
   * Finally, the (potentially modified) response object is returned, allowing the interceptor chain to continue.
   *
   * @param {Object} response - The Axios response object.
   * @returns {Object} The original or modified Axios response object.
   */
  function (response) {
    if (response.config.responseName) {
      Tools.setResponse(response.config.responseName, response);
    }

    if (typeof Tools.options.responseCallback === 'function') {
      response = Tools.options.responseCallback(response);
    }

    return response;
  },
  /**
   * Handles errors for Axios requests, specifically focusing on token-related errors, if the authentication is not done via cookies.
   *
   * This asynchronous function is designed to be used as an error interceptor for Axios requests. It performs several key actions:
   *
   * 1. If a `responseCallback` function is defined in `Tools.options`, it invokes this callback with the error, allowing for custom error handling.
   *
   * 2. Checks if the error is related to an 'invalid_token'. If so, it further checks the error description:
   *    - If the error description indicates an 'Invalid user token' and a refresh token is available, it attempts to refresh the token using the OAuth login flow with the refresh token. Upon successful token refresh, it updates the request's Authorization header with the new access token and retries the original request.
   *    - If no refresh token is found, it logs a message and halts further processing.
   *    - If the error description does not indicate an 'Invalid user token', it triggers a user logout process, clears the SDK options, and redirects the user to a logout URL.
   *
   * 3. If the token refresh attempt fails or if the error is not related to an 'invalid_token', it triggers a user logout process, clears the SDK options, and redirects the user to a logout URL.
   *
   * 4. For all other errors, it simply rejects the promise with the encountered error.
   *
   * This function aims to provide a centralized mechanism for handling authentication-related errors across all Axios requests made using the SDK.
   *
   * @param {Object} error - The error object encountered during the Axios request.
   * @returns {Promise} A promise that either resolves with the retry of the original request or rejects with the processed error.
   */
  async function (error) {
    if (typeof Tools.options.responseCallback === 'function') {
      error = Tools.options.responseCallback(undefined, error);
    }

    const status = get(error, 'response.status');

    // ── Cookie mode (GRN-6822): silent refresh of the 30-min access token ─────
    // The user access token lives in an HttpOnly cookie. On expiry the backend
    // returns 401; refresh via the HttpOnly refresh cookie (single-flight) and
    // retry the original request once. A guest/anonymous session has no refresh
    // token, so its expired app token is re-minted instead. If renewal fails
    // (refresh expired/revoked/reused, or a 400 invalid_grant) the session is
    // unrecoverable → log out and force re-authentication.
    if (Tools.getAuthConfig('withCredentials') && status === 401) {
      const originalConfig = get(error, 'response.config') || {};
      const isTokenEndpoint = (originalConfig.url || '').includes('/oauth2/token');

      if (!isTokenEndpoint) {
        if (originalConfig._retry) {
          // Already renewed once and still 401 → stop (never loop).
          return Promise.reject(error);
        }
        originalConfig._retry = true;

        const bailToLogout = async () => {
          try {
            await Users.logout(Tools.getOption('userId'));
          } catch (_) {
            // Best-effort — the session may already be gone on the server.
          }

          Tools.clearOptions();
          redirectToUrl(`${window.location.origin}${Tools.getOption('logoutURL')}`);

          return Promise.reject(null);
        };

        // GRN-6822: only the opaque-refresh tenants (Greenlend) attempt the user-session refresh. The
        // backend surfaces the feature flag via /cookie/data and the framework stores it into authConfig
        // at bootstrap (before any authenticated call, incl. on reload). Default to enabled when the flag
        // is absent (e.g. before the backend serving it has rolled out) so Greenlend never regresses;
        // non-refresh tenants (Discount/DBank) receive it as false and keep the prior behavior.
        const refreshEnabled = Tools.getAuthConfig('opaqueRefreshEnabled') !== false;

        // `userData` can be empty while a live borrower session still exists (fresh SPA load, hard
        // refresh, new tab, or the onboarding surface which never populates it), so it cannot gate the
        // renewal path on its own.
        const userData = Tools.getOption('userData');
        const hasUserSession = !!userData && Object.keys(userData).length > 0;

        try {
          if (refreshEnabled) {
            // Refresh-first: the refresh token is an HttpOnly cookie JS cannot read; attempt the
            // grant_type=refresh_token renewal, and only fall back to re-minting the guest app token
            // (grant_type=client_proxy) when the refresh is rejected AND this is not a known user
            // session — a known user session whose refresh is rejected is unrecoverable → log out.
            try {
              await refreshUserSession();
              return axiosInstance(originalConfig);
            } catch (refreshError) {
              if (hasUserSession) {
                return bailToLogout();
              }
              await remintAppToken();
              return axiosInstance(originalConfig);
            }
          }

          // Non-refresh tenants: unchanged pre-refresh behavior — a user session refreshes via the
          // legacy path, a guest/anonymous session re-mints the app token.
          await (hasUserSession ? refreshUserSession() : remintAppToken());
          return axiosInstance(originalConfig);
        } catch (e) {
          return bailToLogout();
        }
      }
    }

    if (status === 401 && !Tools.getAuthConfig('withCredentials')) {
      const token = Tools.getOption('accessToken');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          if (decoded.exp && Date.now() >= decoded.exp * 1000) {
            Tools.clearOptions();
            window.location.reload();
            return Promise.reject(error);
          }
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
    }

    if (!Tools.getAuthConfig('withCredentials') && get(error, 'response.data.error') === 'invalid_token') {
      try {
        if (get(error, 'response.data.error_description').includes('Invalid user token')) {
          if (!Tools.getOption('refreshToken')) {
            console.log('No refresh token found');
            return;
          }

          const loginResults = await Oauth.login(
            undefined,
            undefined,
            'refresh_token',
            Tools.getOption('refreshToken'),
          );

          for (let property in loginResults.data) {
            Tools.setOption(camelCase(property), loginResults.data[property]);
          }

          const tokenType = Tools.getAuthConfig('tokenType');
          const accessToken = Tools.options['accessToken'];
          error.response.config.headers.Authorization = `${tokenType} ${accessToken}`;

          return axiosInstance(error.response.config);
        } else {
          await Users.logout(Tools.getOption('userId'));

          Tools.clearOptions();
          redirectToUrl(`${window.location.origin}${Tools.getOption('logoutURL')}`);

          return Promise.reject(null);
        }
      } catch (e) {
        await Users.logout(Tools.getOption('userId'));

        Tools.clearOptions();
        redirectToUrl(`${window.location.origin}${Tools.getOption('logoutURL')}`);

        return Promise.reject(null);
      }
    }

    return Promise.reject(error);
  },
);

const storageOptionsToExclude = ['appToken', 'accessToken', 'refreshToken', 'csrfToken', 'caseToken'];

/**
 * A class containing static properties and methods to configure and manage the SDK.
 */
export class Tools {
  /**
   * @static
   * @property {Object} options - The configuration options for the SDK.
   * @property {string} [options.product] - The product name.
   * @property {string} [options.channel] - The channel (afiliate) name.
   * @property {string} [options.accessToken] - The access token for authentication.
   * @property {string} [options.caseId] - The ID of the current case.
   * @property {number} [options.expiresIn] - The expiration time of the access token in seconds.
   * @property {string} [options.refreshToken] - The token used to refresh the access token.
   * @property {string} [options.scope] - The scope of the access token.
   * @property {Object} [options.userData] - Data related to the authenticated user.
   * @property {string} [options.appToken] - The application token.
   * @property {string} [options.caseToken] - The case token.
   * @property {string} [options.csrfToken] - The csrf token.
   * @property {string} [options.otpChannel] - The channel through wich the OTP should be sent.
   * @property {boolean} [options.demoMode=false] - Flag indicating if the SDK is in demo mode.
   * @property {string} [options.logoutURL] - The URL to redirect to on logout.
   * @property {string} [options.protocol='https'] - The protocol used for requests.
   * @property {string} [options.host] - The host of the API.
   * @property {string} [options.deviceId] - The id of the device using the WebView
   * @property {Object} [options.responses] - Container for custom response handlers.
   * @property {Function} [options.requestCallback] - Callback function for request customization.
   * @property {Function} [options.responseCallback] - Callback function for response handling.
   * @property {Object} options.services - Object containing service configurations.
   * @property {Object} options.services.cases - Configuration for the cases service.
   * @property {string} options.services.cases.version - The version of the cases service.
   * @property {Object} options.services.caseContributors - Configuration for the case contributors service.
   * @property {string} options.services.caseContributors.version - The version of the case contributors service.
   * @property {Object} options.services.mobileVerification - Configuration for the mobile verification service.
   * @property {string} options.services.mobileVerification.version - The version of the mobile verification service.
   * @property {Object} options.services.document - Configuration for the document service.
   * @property {string} options.services.document.version - The version of the document service.
   * @property {Object} options.services.address - Configuration for the address service.
   * @property {string} options.services.address.version - The version of the address service.
   * @property {Object} options.services.oauth - Configuration for the OAuth service.
   * @property {string} options.services.oauth.version - The version of the OAuth service.
   * @property {Object} options.services.users - Configuration for the users service.
   * @property {string} options.services.users.version - The version of the users service.
   * @property {Object} options.services.quote - Configuration for the quote service.
   * @property {string} options.services.quote.version - The version of the quote service.
   * @property {Object} options.services.configurations - Configuration for the configurations service.
   * @property {string} options.services.configurations.version - The version of the configurations service.
   * @property {Object} options.services.selfie - Configuration for the selfie service.
   * @property {string} options.services.selfie.version - The version of the selfie service.
   * @property {Object} options.services.identityVerification - Configuration for the identity verification service.
   * @property {string} options.services.identityVerification.version - The version of the identity verification service.
   * @property {Object} options.services.externalIdentityManagement - Configuration for the external identity management service.
   * @property {string} options.services.externalIdentityManagement.version - The version of the external identity management service.
   * @property {Object} options.services.sendShortURL - Configuration for the send short URL service.
   * @property {string} options.services.sendShortURL.version - The version of the send short URL service.
   * @property {Object} options.services.signature - Configuration for the signature service.
   * @property {string} options.services.signature.version - The version of the signature service.
   * @property {Object} options.services.vat - Configuration for the VAT service.
   * @property {string} options.services.vat.version - The version of the VAT service.
   * @property {Object} options.services.person - Configuration for the person service.
   * @property {string} options.services.person.version - The version of the person service.
   * @property {Object} options.services.decision - Configuration for the decision service.
   * @property {string} options.services.decision.version - The version of the decision service.
   * @property {Object} options.services.extensions - Configuration for the extensions service.
   * @property {string} options.services.extensions.version - The version of the extensions service.
   * @property {Object} options.services.backoffice - Configuration for the backoffice service.
   * @property {string} options.services.backoffice.version - The version of the backoffice service.
   * @property {Object} options.services.cmeLoan - Configuration for the CME loan service.
   * @property {string} options.services.cmeLoan.version - The version of the CME loan service.
   * @property {Object} options.services.crm - Configuration for the CRM service.
   * @property {string} options.services.crm.version - The version of the CRM service.
   * @property {Object} options.services.discount - Configuration for the discount service.
   * @property {string} options.services.discount.version - The version of the discount service.
   * @property {Object} options.services.system - Configuration for the system service.
   * @property {string} options.services.system.version - The version of the system service.
   */
  static options = {
    product: undefined,
    accessToken: undefined,
    caseId: undefined,
    expiresIn: undefined,
    refreshToken: undefined,
    scope: undefined,
    userData: undefined,
    appToken: undefined,
    caseToken: undefined,
    csrfToken: undefined,
    otpChannel: undefined,
    demoMode: false,
    logoutURL: undefined,
    protocol: `https`,
    host: undefined,
    deviceId: undefined,
    responses: {},
    requestCallback: undefined,
    responseCallback: undefined,
    services: {
      cases: { version: 'v1' },
      caseContributors: { version: 'v1' },
      mobileVerification: { version: 'v1' },
      document: { version: 'v1' },
      address: { version: 'v1' },
      oauth: { version: 'v1' },
      users: { version: 'v1' },
      quote: { version: 'v1' },
      configurations: { version: 'v1' },
      selfie: { version: 'v1' },
      identityVerification: { version: 'v1' },
      externalIdentityManagement: { version: 'v1' },
      sendShortURL: { version: 'v1' },
      signature: { version: 'v1' },
      vat: { version: 'v1' },
      person: { version: 'v1' },
      decision: { version: 'v1' },
      extensions: { version: 'v1' },
      backoffice: { version: 'v1' },
      cmeLoan: { version: 'v1' },
      crm: { version: 'v1' },
      greenlend: { version: 'v1' },
      discount: { version: 'v1' },
      system: { version: 'v1' },
    },
  };

  // TODO: Check why this state is here and possibly remove
  static state = {};

  // Config related to the authentication logic (grantType, refererDomain, tokenType, withCredentials)
  static authConfig = {
    grantType: undefined,
    refererDomain: undefined,
    tokenType: undefined,
    withCredentials: undefined,
    productPath: undefined,
  };

  static storage = sessionStorage;

  // An array of option names, that shouldn't be saved in the storage
  static #storageExcludeOptions = Tools.authConfig.withCredentials ? storageOptionsToExclude : [];

  static setAuthConfig(newConfig) {
    const oldConfig = Tools.getAuthConfig();
    Tools.authConfig = { ...oldConfig, ...newConfig };

    if (Object.hasOwn(newConfig, 'withCredentials')) {
      Tools.#storageExcludeOptions = newConfig.withCredentials ? storageOptionsToExclude : [];
    }
  }

  static getAuthConfig(key) {
    return key ? Tools.authConfig[key] : Tools.authConfig;
  }

  /**
   * Sets the storage mechanism for the SDK.
   * @static
   * @param {Storage} storage - The storage mechanism to be used.
   */
  static setStorage(storage) {
    Tools.storage = storage;
  }

  /**
   * Sets multiple options at once.
   * @static
   * @param {Object} options - An object containing key-value pairs of options to set.
   */
  static setOptions(options) {
    for (let option in options) {
      Tools.setOption(option, options[option]);
    }
  }

  /**
   * Retrieves all the current options.
   * @static
   * @returns {Object} The current set of options.
   */
  static getOptions() {
    return Tools.options;
  }

  /**
   * Sets a single option.
   * @static
   * @param {string} key - The key of the option to set.
   * @param {(string|Object)} value - The value of the option. If a stringified JSON, it will be parsed.
   */
  static setOption(key, value) {
    set(Tools.options, key, isJSON(value) ? JSON.parse(value) : value);

    if (!isNode() && !Tools.#storageExcludeOptions.includes(key)) {
      this.storage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  }

  /**
   * Retrieves the value of a specific option.
   * @static
   * @param {string} key - The key of the option to retrieve.
   * @returns {(string|Object)} The value of the option.
   */
  static getOption(key) {
    return get(Tools.options, key);
  }

  /**
   * Removes a specific option.
   * @static
   * @param {string} key - The key of the option to remove.
   */
  static removeOption(key) {
    delete Tools.options[key];

    if (!isNode()) {
      Tools.storage.removeItem(key);
    }
  }

  /**
   * Clears specific options related to a user session.
   * @static
   */
  static clearOptions() {
    delete Tools.options.accessToken;
    delete Tools.options.caseId;
    delete Tools.options.caseToken;
    delete Tools.options.csrfToken;
    delete Tools.options.expiresIn;
    delete Tools.options.refreshToken;
    delete Tools.options.scope;
    delete Tools.options.userData;
    delete Tools.options.userId;
    delete Tools.options.journeyUuid;
    delete Tools.options.jti;

    if (!isNode()) {
      Tools.storage.removeItem('accessToken');
      Tools.storage.removeItem('caseId');
      Tools.storage.removeItem('caseToken');
      Tools.storage.removeItem('csrfToken');
      Tools.storage.removeItem('expiresIn');
      Tools.storage.removeItem('refreshToken');
      Tools.storage.removeItem('scope');
      Tools.storage.removeItem('userData');
      Tools.storage.removeItem('userId');
      Tools.storage.removeItem('journeyUuid');
      Tools.storage.removeItem('jti');
    }
  }

  /**
   * Stores a response object in the SDK's options for later retrieval.
   *
   * This method allows for the storage of response objects by name within a centralized location (`Tools.options.responses`).
   * It is useful for caching responses or making them accessible across different parts of an application without
   * needing to re-fetch or pass the response directly. The method stores both the data and headers of the response.
   *
   * @param {string} name - The name under which to store the response. Acts as a key for later retrieval.
   * @param {Object} response - The response object to store. Should contain both `data` and `headers` properties.
   */
  static setResponse(name, response) {
    if (name && response) {
      set(Tools.options.responses, name, { data: response.data, headers: response.headers });
    }
  }

  /**
   * Retrieves a stored response object by name from the SDK's options.
   *
   * This method is the counterpart to `setResponse`, allowing for the retrieval of previously stored response objects.
   * It is useful for accessing cached responses or data that has been stored across different parts of an application.
   *
   * @param {string} name - The name of the response to retrieve. This corresponds to the key used when the response was stored.
   * @returns {Object|undefined} The stored response object, if found; otherwise, `undefined`.
   */
  static getResponse(name) {
    return get(Tools.options.responses, name);
  }
}

/**
 * Represents operations related to a Person entity in the system.
 */
export class Person {
  /**
   * Constructs a URL for accessing the Person Manager service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { person } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${person.version}/person-manager${endpoint}`;
  }

  /**
   * Initiates the creation of a user associated with a specific person.
   *
   * @param {string} personUuid - The UUID of the person for whom a user is being created.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static createUserForPerson(personUuid) {
    const url = Person.createServerUrl(`/${personUuid}/create-user-for-person`);

    return axiosInstance.post(url);
  }
}

/**
 * Represents OCR (document data extraction) operations.
 */
export class Ocr {
  /**
   * Constructs a URL for the OCR service.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;

    return `${Tools.options.protocol}://${host}/v1/ocr${endpoint}`;
  }

  /**
   * Extracts OCR data for a specific case. Routed through the shared axios instance so the
   * request interceptor applies cookie-mode auth (withCredentials + X-CSRF-Token) or bearer auth.
   *
   * @param {string} caseId - The unique identifier of the case.
   * @returns {Promise} A promise that resolves with the OCR extraction response.
   */
  static extractData(caseId) {
    const url = Ocr.createServerUrl(`/${caseId}`);

    return axiosInstance.post(url);
  }
}

/**
 * Represents a case entity in a case management system. Provides static methods for case operations such as creation, retrieval, updating, and cancellation.
 */
export class Case {
  /**
   * Constructs a URL for the case service.
   *
   * @param {string} [endpoint=''] - The specific API endpoint to append to the base URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { cases } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${cases.version}/case-manager/cases${endpoint}`;
  }

  /**
   * Creates a new case with the provided user data.
   *
   * @param {Object} [userData={}] - The data for the new case.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async create(userData = {}) {
    const url = Case.createServerUrl();

    return axiosInstance.post(url, userData);
  }

  /**
   * Retrieves a case by user ID, optionally filtering by journey UUID.
   *
   * @param {string} caseId - The ID of the case.
   * @param {string} [journeyUuid] - The UUID of the journey to filter by.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async retrieve(caseId, journeyUuid) {
    const url = Case.createServerUrl(`/${caseId}`);

    return journeyUuid ? axiosInstance.get(url, { params: { journeyUuid } }) : axiosInstance.get(url);
  }

  /**
   * Retrieves cases relevant to a specific user.
   *
   * @param {string} userId - The ID of the user.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async relevant(userId) {
    const url = Case.createServerUrl(`/relevant`);

    return axiosInstance.get(url, { params: { userId } });
  }

  /**
   * Updates a case with the provided user data, optionally filtering by journey UUID.
   *
   * @param {string} uuid - The UUID of the case.
   * @param {Object} userData - The data to update the case with.
   * @param {string} [journeyUuid] - The UUID of the journey to filter by.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static update(uuid, userData, journeyUuid) {
    const url = Case.createServerUrl(`/${uuid}`);
    const options = { params: { journeyUuid } };

    return journeyUuid ? axiosInstance.patch(url, userData, options) : axiosInstance.patch(url, userData);
  }

  /**
   * Cancels a case by setting its status to cancelled.
   *
   * @param {string} uuid - The UUID of the case to cancel.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static cancel(uuid) {
    const url = Case.createServerUrl(`/${uuid}/status-cancelled`);

    return axiosInstance.post(url);
  }

  /**
   * Finds cases by user ID, optionally filtering to only active cases.
   *
   * @param {string} userId - The ID of the user.
   * @param {boolean} [onlyActive=true] - Whether to filter to only active cases.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static findCasesByUser(userId, onlyActive = true) {
    const url = Case.createServerUrl(`/find-cases-by-user?userId=${userId}`);

    return axiosInstance.get(url);
  }

  /**
   * Attaches a user to a case.
   *
   * @param {string} userId - The ID of the user.
   * @param {string} caseId - The ID of the case.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async attach(userId, caseId) {
    const url = Case.createServerUrl(`/${caseId}/attach-to-user`);

    const data = { userId };

    data.caseToken = Tools.getOption('caseToken');

    return axiosInstance.post(url, data);
  }

  /**
   * Links an account to a case.
   *
   * @param {string} uuid - The UUID of the case.
   * @param {string} linkedService - The service to link the account to.
   * @param {string} accountType - The type of account to link.
   * @param {string} serviceName - The name of the service.
   * @param {Object} [data] - Additional data for the account link.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static linkAccount(uuid, linkedService, accountType, serviceName, data) {
    const url = Case.createServerUrl(`/${uuid}/accounts/${linkedService}/${serviceName}`);

    return axiosInstance.post(url, data || { accountType });
  }

  static getQuote(caseId, indicative, timeout, feeType, repaymentType, policy = 'v2') {
    const url = Case.createServerUrl(`/${caseId}/decide`);

    return axiosInstance.post(
      url,
      {},
      {
        timeout,
        params: {
          indicative,
          feeType,
          repaymentType,
          policy,
        },
      },
    );
  }

  /**
   * Requests a quote for a case based on the specified fee and repayment types.
   *
   * @param {string} caseId - The ID of the case for which the quote is requested.
   * @param {string} feeType - The type of fee associated with the case.
   * @param {string} repaymentType - The type of repayment plan for the case.
   * @param {string} [policy='v2'] - The policy version to use for the quote. Defaults to 'v2'.
   * @returns {Promise} A promise that resolves with the API response from the quote request.
   */
  static quote(caseId, feeType, repaymentType, policy = 'v2') {
    const url = Case.createServerUrl(`/${caseId}/decide`);

    return axiosInstance.post(
      url,
      {},
      {
        params: {
          indicative: true,
          feeType,
          repaymentType,
          policy,
        },
      },
    );
  }

  /**
   * Makes a decision request for a specific case with an optional timeout and policy version.
   *
   * @param {string} caseId - The ID of the case for which the decision is being requested.
   * @param {number} timeout - The timeout in milliseconds for the decision request.
   * @param {string} [policy='v2'] - The policy version to use for making the decision. Defaults to 'v2'.
   * @returns {Promise} A promise that resolves with the response from the decision request.
   */
  static decision(caseId, timeout, policy = 'v2') {
    const url = Case.createServerUrl(`/${caseId}/decide`);

    return axiosInstance.post(
      url,
      {},
      {
        timeout,
        params: {
          indicative: false,
          policy,
        },
      },
    );
  }

  /**
   * Uploads a document to a specific case.
   *
   * This method uploads a document file to a case identified by `caseId`. It constructs a form data payload
   * with the file and sends it using a POST request. The document type and subtype can be specified for
   * categorization purposes. The file name is extracted from the file object; if not available, 'new-file' is used as a default.
   *
   * @param {string} caseId - The ID of the case to which the document will be uploaded.
   * @param {File} file - The document file to upload.
   * @param {string} docType - The type of the document being uploaded.
   * @param {string} docSubType - The subtype of the document being uploaded.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async uploadDocument(caseId, file, docType, docSubType) {
    const url = Case.createServerUrl(`/${caseId}/documents`);
    const formData = new FormData();

    formData.append('file', file);

    return axiosInstance.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { fileName: file.name ?? 'new-file', docType, docSubType },
    });
  }

  /**
   * Deletes a document from a case.
   *
   * @param {string} caseId - The ID of the case from which the document will be deleted.
   * @param {string} fileId - The ID of the file to be deleted.
   * @returns {Promise} A promise that resolves with the server's response to the delete request.
   */
  static async deleteDocument(caseId, fileId) {
    const url = Case.createServerUrl(`/${caseId}/documents/${fileId}`);

    return axiosInstance.delete(url);
  }

  /**
   * Retrieves a list of uploaded documents for a case, optionally filtered by document type.
   *
   * @param {string} caseId - The ID of the case for which documents are being retrieved.
   * @param {string} [docType] - The type of documents to filter by. If not provided, all documents are retrieved.
   * @returns {Promise} A promise that resolves with the server's response containing the list of documents.
   */
  static async getUploadedDocuments(caseId, docType, shouldFilterByUserId) {
    const url = Case.createServerUrl(`/${caseId}/${docType ? 'find-by-doctype' : 'documents'}`);

    return axiosInstance.get(url, { params: { docType, shouldFilterByUserId } });
  }

  /**
   * Retrieves a specific uploaded document by its UUID for a case.
   *
   * @param {string} caseId - The ID of the case for which the document is being retrieved.
   * @param {string} documentUuid - The UUID of the document to retrieve.
   * @returns {Promise} A promise that resolves with the server's response containing the document data.
   */
  static async getUploadedDocumentByUuid(caseId, documentUuid) {
    const url = Case.createServerUrl(`/${caseId}/documents/${documentUuid}`);

    return axiosInstance.get(url, { responseType: 'arraybuffer' });
  }

  /**
   * Creates a ZIP file containing a list of specified files for a case.
   *
   * @param {string} caseId - The ID of the case for which the ZIP file is created.
   * @param {Array<string>} fileNameList - A list of file names to include in the ZIP file.
   * @returns {Promise} A promise that resolves with the server's response containing the ZIP file data.
   */
  static async getZipFiles(caseId, fileNameList) {
    const url = Case.createServerUrl(`/${caseId}/get-zip-files`);

    return axiosInstance.post(url, { fileNameList }, { responseType: 'arraybuffer' });
  }

  /**
   * Performs a callback to the account service using a specified method and URL.
   *
   * @param {string} method - The HTTP method to use for the callback (e.g., 'GET', 'POST').
   * @param {string} url - The URL to which the callback is made.
   * @returns {Promise} A promise that resolves with the server's response to the callback request.
   */
  static accountCallback(method, url) {
    return axiosInstance({ method, url });
  }

  /**
   * Advances or reverses the progress of a case in a journey.
   *
   * @param {string} caseId - The ID of the case to progress.
   * @param {string} journeyUuid - The UUID of the journey.
   * @param {string} [direction='FORWARD'] - The direction of progress ('FORWARD' or 'REVERSE').
   * @returns {Promise} A promise that resolves with the server's response.
   */
  static async progress(caseId, journeyUuid, direction = 'FORWARD') {
    const url = Case.createServerUrl(`/${caseId}/progress`);

    return axiosInstance.post(url, { direction, journeyUuid });
  }

  /**
   * Creates a new user associated with a specific case.
   *
   * @param {string} caseId - The ID of the case for which a new user is created.
   * @returns {Promise} A promise that resolves with the server's response.
   */
  static createUserForCase(caseId) {
    const url = Case.createServerUrl(`/${caseId}/create-user-for-case`);

    return axiosInstance.post(url);
  }

  /**
   * Retrieves a detailed list of all bank accounts associated with a case.
   *
   * @param {string} caseUuid - The UUID of the case for which bank accounts are retrieved.
   * @returns {Promise} A promise that resolves with the server's response containing the list of bank accounts.
   */
  static async getBankAccountsFull(caseUuid) {
    const url = Case.createServerUrl(`/bank-account/full-manual-accounts`);

    return axiosInstance.get(url, { params: { caseUuid } });
  }

  /**
   * Retrieves a list of manually added bank accounts associated with a case.
   *
   * @param {string} caseUuid - The UUID of the case for which bank accounts are retrieved.
   * @returns {Promise} A promise that resolves with the server's response containing the list of bank accounts.
   */
  static async getBankAccounts(caseUuid) {
    const url = Case.createServerUrl(`/bank-account/manual-accounts`);

    return axiosInstance.get(url, { params: { caseUuid } });
  }

  /**
   * Marks a specified bank account as the primary account for a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} accountNumber - The account number of the bank account to be marked as primary.
   * @param {string} sortCode - The sort code of the bank account to be marked as primary.
   * @returns {Promise} A promise that resolves with the server's response.
   */
  static async setPrimaryBankAccount(caseUuid, accountNumber, sortCode) {
    const url = Case.createServerUrl(`/bank-account/mark-primary`);

    return axiosInstance.patch(url, null, {
      params: {
        caseUuid,
        accountNumber,
        sortCode,
      },
    });
  }

  /**
   * Retrieves a summary of a case.
   *
   * @param {string} caseUuid - The UUID of the case for which the summary is requested.
   * @returns {Promise} A promise that resolves with the server's response containing the case summary.
   */
  static async getCaseSummary(caseUuid) {
    const url = Case.createServerUrl(`/${caseUuid}/application-summary`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the offer discount information for a case.
   *
   * @param {string} caseUuid - The UUID of the case for which the offer discount information is requested.
   * @returns {Promise} A promise that resolves with the server's response containing the offer discount information.
   */
  static async getCaseOfferDiscount(caseUuid) {
    const url = Case.createServerUrl(`/${caseUuid}/offer-discount`);

    return axiosInstance.get(url);
  }

  /**
   * Creates a file with the provided data for a case.
   *
   * @param {string} caseUuid - The UUID of the case for which the file is created.
   * @param {Object} data - The data to be included in the file.
   * @returns {Promise} A promise that resolves with the server's response containing the created file data.
   */
  static async createFileWithData(caseUuid, data) {
    const url = Case.createServerUrl(`/create-file-with-data/${caseUuid}`);

    return axiosInstance.post(url, data, { responseType: 'arraybuffer' });
  }
}

/**
 * Represents a utility class for managing case contributors in a case management system.
 * This class provides static methods to interact with the case management system's API for operations related to case contributors,
 * such as creating journey sets, getting potential or invited contributors, sending, resending, canceling, and approving contributor invitations.
 */
export class CaseContributors {
  /**
   * Constructs a URL for the case contributors service.
   *
   * @param {string} [endpoint=''] - The specific API endpoint to append to the base URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { caseContributors } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${caseContributors.version}/case-manager${endpoint}`;
  }

  static async getApplicantData(caseUuid) {
    const url = CaseContributors.createServerUrl(`/cases/${caseUuid}/applicant`);

    return axiosInstance.get(url);
  }

  /**
   * Creates a new journey set for a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeyType - The type of journey to create.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async createJourneySet(caseUuid, journeyType) {
    const url = CaseContributors.createServerUrl(`/${caseUuid}/journey-set?journeyType=${journeyType}`);

    return axiosInstance.post(url);
  }

  /**
   * Retrieves potential contributors for a specific journey within a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeyUuid - The UUID of the journey.
   * @param {string} [officerType='All'] - The type of officer to filter the potential contributors by.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async getPotentialContributors(caseUuid, journeyUuid, officerType = 'All') {
    const url = CaseContributors.createServerUrl(
      `/${caseUuid}/journey-set/${journeyUuid}/potential-invite-list?officerType=${officerType}`,
    );

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the list of contributors who have been invited to a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async getInvitedContributors(caseUuid) {
    const url = CaseContributors.createServerUrl(`/${caseUuid}/journey-set/invited-list`);

    return axiosInstance.get(url);
  }

  /**
   * Sends an invitation to a potential contributor for a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeySetUuid - The UUID of the journey set.
   * @param {Object} contributorToInvite - The details of the contributor to invite.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async sendContributorInvitation(caseUuid, journeySetUuid, contributorToInvite) {
    const url = CaseContributors.createServerUrl(`/${caseUuid}/journey-set/${journeySetUuid}/invite`);

    return axiosInstance.post(url, contributorToInvite);
  }

  /**
   * Resends an invitation to a contributor for a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeySetUuid - The UUID of the journey set.
   * @param {string} journeyUuid - The UUID of the journey.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async resendContributorInvitation(caseUuid, journeySetUuid, journeyUuid) {
    const url = CaseContributors.createServerUrl(
      `/${caseUuid}/journey-set/${journeySetUuid}/journey/${journeyUuid}/resend-invitation`,
    );

    return axiosInstance.post(url);
  }

  /**
   * Cancels an invitation sent to a contributor for a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeySetUuid - The UUID of the journey set.
   * @param {string} journeyUuid - The UUID of the journey.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async cancelContributorInvitation(caseUuid, journeySetUuid, journeyUuid) {
    const url = CaseContributors.createServerUrl(`/${caseUuid}/journey-set/${journeySetUuid}/journey/${journeyUuid}`);

    return axiosInstance.delete(url);
  }

  /**
   * Approves an invitation for a contributor to join a case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} journeySetUuid - The UUID of the journey set.
   * @param {string} journeyUuid - The UUID of the journey.
   * @param {string} guestToken - The token provided to the guest for approval.
   * @returns {Promise} A promise that resolves with the API response.
   */
  static async approveInvitation(caseUuid, journeySetUuid, journeyUuid, guestToken) {
    const url = CaseContributors.createServerUrl(
      `/${caseUuid}/journey-set/${journeySetUuid}/journey/${journeyUuid}/approve-invitation`,
    );

    return axiosInstance.post(url, { guestToken });
  }

  /**
   * Starts a new journey.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} flow - The type of the flow.
   * @returns {Promise} A promise that resolves with the API response (the journey id on success).
   */
  static async startJourney(caseUuid, flow) {
    const url = CaseContributors.createServerUrl(`/${caseUuid}/journey/start`);

    return axiosInstance.post(url, { type: flow });
  }
}

/**
 * Provides OAuth related functionalities.
 */
export class Oauth {
  /**
   * Constructs a URL for accessing the OAuth service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base OAuth service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { oauth } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${oauth.version}/oauth2${endpoint}`;
  }

  /**
   * Gets the App (client) token used for getting the configs
   *
   * @param {string} channelId - The current channel id
   * @returns {Promise} A promise that resolves with the response from the App token endpoint
   */
  static getAppToken(channelId, preserveCookies) {
    const url = Oauth.createServerUrl(`/token`);

    const { grantType, refererDomain, productPath } = Tools.getAuthConfig();

    return axiosInstance.post(url, null, {
      params: {
        grant_type: grantType,
        referer_domain: refererDomain,
        channel: channelId,
        preserveCookies,
        product_path: productPath,
      },
      disallowHeaders: ['Authorization'],
    });
  }

  /**
   * Performs the login operation using various grant types.
   *
   * This method supports different OAuth grant types including password, refresh token, and exchange token.
   * For the ONE_TIME_PASSWORD grant type, it expects `username` as the OTP token and `password` as the code.
   *
   * @param {string} username - The username or the OTP token depending on the grant type.
   * @param {string} password - The user's password or the OTP code depending on the grant type.
   * @param {string} grantType - The OAuth grant type for the operation.
   * @param {string} refreshToken - The refresh token for obtaining new access tokens.
   * @param {Object} auth - The authentication credentials for the request.
   * @returns {Promise} A promise that resolves with the response from the OAuth token endpoint.
   */
  static async login(username, password, grantType, refreshToken, auth) {
    const url = Oauth.createServerUrl(`/token`);
    let data = {
      grant_type: grantType,
      username: username ? username.trim() : undefined,
      password: password ? password.trim() : undefined,
      refresh_token: refreshToken,
      exchange_token: grantType === EXCHANGE_TOKEN ? Tools.getOption('accessToken') : undefined,
      channel: Tools.getOption('channel'),
    };

    if (grantType === ONE_TIME_PASSWORD) {
      data.opToken = data.username;
      data.code = data.password;
      delete data.username;
      delete data.password;
    }

    data = pickBy(data, identity);

    return axiosInstance.post(url, querystring.stringify(data), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      auth,
      tokenScope: 'appToken',
    });
  }
}

/**
 * Handles operations related to external identity management, such as single sign-on (SSO) logins.
 */
export class ExternalIdentityManagement {
  /**
   * Constructs a URL for accessing the External Identity Management service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { externalIdentityManagement } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${externalIdentityManagement.version}/external-identity-management${endpoint}`;
  }

  /**
   * Initiates an external login process using SSO data.
   *
   * @param {Object} SSOData - The single sign-on data used for authentication.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async externalLogin(SSOData) {
    const url = ExternalIdentityManagement.createServerUrl(`/external-login`);

    return axiosInstance.post(url, SSOData, { tokenScope: 'appToken' });
  }

  /**
   * Initiates an external login process for loan services using SSO data.
   *
   * @param {Object} SSOData - The single sign-on data used for loan service authentication.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async externalLoanLogin(SSOData) {
    const url = ExternalIdentityManagement.createServerUrl(`/loan/external-login`);

    return axiosInstance.post(url, SSOData, { tokenScope: 'appToken' });
  }

  /**
   * Initiates an external login process for renewal services using SSO data.
   *
   * @param {Object} SSOData - The single sign-on data used for renewal service authentication.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async externalRenewalLogin(SSOData) {
    const url = ExternalIdentityManagement.createServerUrl(`/renewal/external-login`);

    return axiosInstance.post(url, SSOData, { tokenScope: 'appToken' });
  }

  static async externalBackofficeLogin(SSOData) {
    const url = ExternalIdentityManagement.createServerUrl(`/back-office/external-login`);

    return axiosInstance.post(url, SSOData);
  }
}

/**
 * Provides functionalities related to user operations within the system.
 * This includes actions such as creating users, managing user notifications,
 * handling password resets, user authentication, and managing user permissions.
 */
export class Users {
  /**
   * Constructs a URL for accessing the Users service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base Users service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { users } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${users.version}/users${endpoint}`;
  }

  /**
   * Retrieves user notifications based on provided parameters.
   *
   * @param {Object} params - The parameters to filter notifications.
   * @returns {Promise} A promise that resolves with the fetched notifications.
   */
  static async getUserNotifications(params) {
    const url = Users.createServerUrl(`/notifications`);

    return axiosInstance.get(url, { params });
  }

  /**
   * Retrieves a single user notification based on provided parameters.
   *
   * @param {Object} params - The parameters to identify the notification.
   * @returns {Promise} A promise that resolves with the fetched notification.
   */
  static async getUserNotification(params) {
    const url = Users.createServerUrl(`/notification`);

    return axiosInstance.get(url, { params });
  }

  /**
   * Marks a user notification as read based on provided parameters.
   *
   * @param {Object} params - The parameters to identify the notification to be marked as read.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async markUserNotificationAsRead(params) {
    const url = Users.createServerUrl(`/notification/read`);

    return axiosInstance.patch(url, undefined, { params });
  }

  /**
   * Creates an anonymous user with the provided user data.
   *
   * @param {Object} user - The user data for the anonymous user.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static createAnonymous(user) {
    const url = Users.createServerUrl(`/create-anonymous`);

    return axiosInstance.post(url, user, { tokenScope: 'appToken' });
  }

  /**
   * Creates a new user with the provided data.
   *
   * @param {Object} user - The user data for creating a new user.
   * @param {boolean} [silent=false] - Whether to suppress email notifications.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static create(user, silent = false) {
    const url = Users.createServerUrl();
    const data = {
      username: user.username.trim(),
      password: user.password.trim(),
      email: user.email,
      roles: user.roles,
      silent,
    };

    return axiosInstance.post(url, data, { tokenScope: 'appToken' });
  }

  /**
   * Initiates a password reset process for the user with the given email.
   *
   * @param {string} email - The email of the user to initiate a password reset for.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static resetPasswordInitiation(email) {
    const url = Users.createServerUrl(`/reset-password-initiation`);

    return axiosInstance.post(url, { email }, { tokenScope: 'appToken' });
  }

  /**
   * Completes the password reset process with the provided data.
   *
   * @param {Object} data - The data required for password reset, including new password and operation token.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static resetPasswordChange({ newPassword, opToken, username }) {
    const url = Users.createServerUrl(`/reset-password-change`);

    return axiosInstance.post(
      url,
      {
        newPassword: newPassword.trim(),
        opToken,
        username,
      },
      { tokenScope: 'appToken' },
    );
  }

  /**
   * Sets a new password for the user, optionally as part of a password reset process.
   *
   * @param {Object} data - The data required for setting a new password.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static setPassword({ password, currentPassword, opToken, contextType }) {
    const url = Users.createServerUrl(`/set-password`);

    return axiosInstance.post(url, { password, currentPassword, opToken }, { params: { contextType } });
  }

  /**
   * Sets a new password for the user.
   *
   * @param {Object} data - The data required for setting a new password.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static forgotPassword({ password, opToken, contextType }) {
    const url = Users.createServerUrl(`/forgot-password`);

    return axiosInstance.post(url, { password, opToken }, { params: { contextType } });
  }

  /**
   * Logs out the user with the given user ID.
   *
   * @param {string} userId - The ID of the user to log out.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static logout(userId) {
    const url = Users.createServerUrl(`/${encodeURIComponent(userId)}/logout`);

    return axiosInstance.post(url);
  }

  /**
   * Updates the user with the given ID using the provided fields.
   *
   * @param {string} userId - The ID of the user to update.
   * @param {Object} fields - The fields to update for the user.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static update(userId, fields) {
    const url = Users.createServerUrl(`/${encodeURIComponent(userId)}`);

    return axiosInstance.patch(url, fields);
  }

  /**
   * Verifies the email of the user with the provided token.
   *
   * @param {string} user - The user ID or email to verify.
   * @param {string} opToken - The operation token for email verification.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async verifyEmail(user, opToken) {
    const url = Users.createServerUrl(`/${encodeURIComponent(user)}/verify-email`);

    return axiosInstance.post(url, { opToken }, { tokenScope: 'appToken' });
  }

  /**
   * Retrieves the email verification status for the given email.
   *
   * @param {string} email - The email to check verification status for.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async getEmailVerificationStatus(email) {
    const url = Users.createServerUrl(`/${encodeURIComponent(email)}/verify-email`);

    return axiosInstance.get(url);
  }

  /**
   * Resends the verification email to the user with the given ID.
   *
   * @param {string} userId - The ID of the user to resend the verification email to.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async resendVerificationEmail(userId) {
    const url = Users.createServerUrl(`/${encodeURIComponent(userId)}/verify-email-initiation`);

    return axiosInstance.post(url);
  }

  /**
   * Sends a one-time password (OTP) to the user specified by the provided data.
   *
   * @param {string} otpType - The type of OTP to send.
   * @param {Object} data - The data identifying the user and how to send the OTP.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async otpSend(otpType, { id, email, password, phone, channel, guest_token, caseUuid }) {
    const url = Users.createServerUrl(`/otp/send`);
    const data = { id, email, password, phone, caseUuid, channel, guest_token };
    return axiosInstance.post(url, data, { params: { otpType, sendType: channel }, tokenScope: 'appToken' });
  }

  /**
   * Authenticates a user login using a one-time password (OTP).
   *
   * @param {string} opToken - The operation token associated with the OTP.
   * @param {string} code - The OTP code provided by the user.
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async otpLogin(opToken, code) {
    const url = Users.createServerUrl(`/otp/login`);

    return axiosInstance.post(
      url,
      {
        opToken,
        code: code.trim(),
      },
      { tokenScope: 'appToken' },
    );
  }

  /**
   * Retrieves the permissions of the currently authenticated user.
   *
   * @returns {Promise} A promise that resolves with the response from the server, typically containing the user's permissions.
   */
  static async getUserPermissions() {
    const url = Users.createServerUrl('/permission');
    return axiosInstance.get(url);
  }

  /**
   * Retrieves information about the user
   *
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async getUserInfo(userId) {
    const url = Users.createServerUrl(`/${userId}`);
    return axiosInstance.get(url);
  }
}

/**
 * Handles mobile phone verification processes, including generating and sending verification codes, and verifying received codes.
 */
export class MobileVerification {
  /**
   * Constructs a URL for accessing the Mobile Verification service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { mobileVerification } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${mobileVerification.version}/mobile-phone-verification${endpoint}`;
  }

  /**
   * Generates a verification code and sends it to the user's mobile phone.
   *
   * @param {Object} params - Parameters for generating and sending the code, such as the user's phone number.
   * @returns {Promise} A promise that resolves with the server's response.
   */
  static async generateAndSendCode(params) {
    const url = MobileVerification.createServerUrl(`/generate-and-send-code`);

    return axiosInstance.post(url, null, { params });
  }

  /**
   * Verifies the code received by the user's mobile phone.
   *
   * @param {string} code - The verification code to be verified.
   * @param {string} opToken - The operation token associated with the verification process.
   * @returns {Promise} A promise that resolves with the server's response indicating the success or failure of the verification.
   */
  static async verifyCode(code, opToken) {
    const url = MobileVerification.createServerUrl(`/verify-code`);

    return axiosInstance.post(url, null, { params: { code, opToken } });
  }
}

/**
 * Provides functionalities related to address operations, such as postcode completion, address lookup, and address formatting.
 */
export class Address {
  /**
   * Constructs a URL for accessing the Address service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { address } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${address.version}/services/address${endpoint}`;
  }

  /**
   * Performs a postcode completion operation, suggesting full postcodes based on partial input.
   *
   * @param {string} query - The partial postcode to complete.
   * @returns {Promise} A promise that resolves with the server's response containing suggested postcodes.
   */
  static postcodeCompletion(query, language) {
    const url = Address.createServerUrl(`/postcode-completion`);

    return axiosInstance.get(url, { params: { query, language } });
  }

  /**
   * Looks up addresses based on a given postcode, optionally filtered by a policy.
   *
   * @param {string} postcode - The postcode to lookup addresses for.
   * @param {string} [policy] - An optional policy to filter the address lookup.
   * @param {string} [language] - The language in which the addresses should be returned.
   * @returns {Promise} A promise that resolves with the server's response containing addresses for the given postcode.
   */
  static addressLookup(postcode, policy, language) {
    const url = Address.createServerUrl('/address-lookup');

    return axiosInstance.get(url, { params: { query: encodeURIComponent(postcode), policy, language } });
  }

  /**
   * Formats a given address according to specific formatting rules.
   *
   * @param {Object} address - The address object to format.
   * @returns {Promise} A promise that resolves with the server's response containing the formatted address.
   */
  static addressFormat(address, language) {
    const url = Address.createServerUrl('/address-format');

    return axiosInstance.post(url, address, { params: { language } });
  }

  /**
   * Retrieves addresses based on specified parameters, such as city or region.
   *
   * @param {Object} params - The parameters to use for retrieving addresses.
   * @param {string} [language] - The language in which the addresses should be returned.
   * @returns {Promise} A promise that resolves with the server's response containing addresses matching the given parameters.
   */
  static async getAddressByParams(params, language) {
    const url = Address.createServerUrl('/city');

    return axiosInstance.get(url, { params: { ...params, language } });
  }

  /**
   * Searches for cities that match the given search term.
   *
   * @param {string} city - City name or prefix used to search for matching cities.
   * @param {string} [language] - The language in which the cities should be returned.
   * @returns {Promise} A promise that resolves with the server's response containing a list of matching city names.
   */
  static getCities(city, language) {
    const url = Address.createServerUrl('/cities');

    return axiosInstance.get(url, { params: { city, language } });
  }

  /**
   * Searches for streets by city, optionally filtered by a street name or prefix.
   *
   * @param {string} city - Name of the city for which streets should be returned.
   * @param {string} [street] - Optional street name or prefix used to filter results.
   * @param {string} [language] - The language in which the streets should be returned.
   * @returns {Promise} A promise that resolves with the server's response containing a list of matching street names.
   */
  static getStreets(city, street, language) {
    const url = Address.createServerUrl('/streets');

    return axiosInstance.get(url, { params: { city, street, language } });
  }
}

/**
 * Provides integration services for various external APIs, including company and bank lookups, transcription services, and financial information retrieval.
 */
export class Integration {
  /**
   * Constructs a URL for accessing the integration service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { address } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${address.version}${endpoint}`;
  }

  /**
   * Looks up company information based on a query and optional business type.
   *
   * @param {string} [query=''] - The search query for the company lookup.
   * @param {string} [businessType] - Optional business type to refine the search.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async companyLookup(query = '', businessType) {
    const businessTypeQuery = businessType ? `&businessType=${businessType}` : '';
    query = `activeOnly=true&searchPreviousNames=true&key=${encodeURIComponent(query.trim())}${businessTypeQuery}`;
    const url = Integration.createServerUrl(`/services/company/company-lookup?${query}`);

    return axiosInstance.get(url);
  }

  /**
   * Looks up bank information based on a key.
   *
   * @param {string} [key=''] - The key for the bank lookup.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async bankLookup(key = '') {
    const url = Integration.createServerUrl(`/services/bank-branches/get-banks`);

    return axiosInstance.get(url, { params: { key } });
  }

  /**
   * Looks up bank branches based on a key and optional bank code.
   *
   * @param {string} [key=''] - The key for the bank branches lookup.
   * @param {string} [bankCode=''] - Optional bank code to refine the search.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async bankBranchesLookup(key = '', bankCode = '') {
    const url = Integration.createServerUrl(`/services/bank-branches/get-branches`);

    return axiosInstance.get(url, { params: { key, bankCode } });
  }

  /**
   * Transcribes audio content using a specified model provider.
   *
   * @param {string} audioFile - The audio file content to be transcribed.
   * @returns {Promise} A promise that resolves with the transcription response from the server.
   */
  static async transcription(audioFile) {
    const url = Integration.createServerUrl(`/services/transcription`);
    // TODO: The provider should not be FE
    const params = { modelProvider: 'google' };
    const payload = { content: audioFile };

    return axiosInstance.post(url, payload, { params });
  }

  /**
   * Retrieves the current prime rate based on an interest rate.
   *
   * @param {number} interestRate - The interest rate to get the prime rate for.
   * @returns {Promise} A promise that resolves with the prime rate from the server.
   */
  static async getPrimeByInterestRate(interestRate) {
    const url = Integration.createServerUrl(`/current-prime`);
    const params = { interestRate };

    return axiosInstance.get(url, { params });
  }

  /**
   * Retrieves information about bank branches.
   *
   * @returns {Promise} A promise that resolves with the bank branches information from the server.
   */
  static async getBankBranches(data) {
    const url = Integration.createServerUrl(`/bank-info/bank-branches`);

    return axiosInstance.post(url, data);
  }
}

/**
 * Represents operations related to officers and shareholders within a case management context.
 */
export class Officers {
  /**
   * Constructs a URL for accessing the Officers service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { document } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${document.version}/case-manager/cases${endpoint}`;
  }

  /**
   * Retrieves shareholders associated with a specific case.
   *
   * @param {string} caseID - The ID of the case to retrieve shareholders for.
   * @param {boolean} [includeCompanies=false] - Whether to include company shareholders in the response.
   * @param {string} [type='SHAREHOLDERS'] - The type of shareholders to retrieve.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async getShareholders(caseID, includeCompanies = false, type = 'SHAREHOLDERS') {
    const url = Officers.createServerUrl(`/${caseID}/company/officers`);

    return axiosInstance.get(url, { params: { type, includeCompanies } });
  }

  /**
   * Updates the information of a specific shareholder in a case.
   *
   * @param {string} caseID - The ID of the case the shareholder belongs to.
   * @param {Object} data - The data to update the shareholder with.
   * @param {string} uuid - The unique identifier of the shareholder to update.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async setShareholder(caseID, data, uuid) {
    const url = Officers.createServerUrl(`/${caseID}/company/officers/${uuid}`);

    return axiosInstance.put(url, data);
  }

  /**
   * Adds a new shareholder to a specific case.
   *
   * @param {string} caseID - The ID of the case to add a shareholder to.
   * @param {Object} data - The shareholder data to add.
   * @returns {Promise} A promise that resolves with the response from the server.
   */
  static async addShareholder(caseID, data) {
    const url = `${Officers.createServerUrl(`/${caseID}/company/officers`)}`;

    return axiosInstance.post(url, data);
  }
}

/**
 * @deprecated since in Express, used in legacy projects - Use the Configuration class instead.
 */
export class Configurations {
  /**
   * Constructs a URL for accessing the Configurations service endpoints.
   *
   * @deprecated since in Express, used in legacy projects - Use Configuration.createServerUrl instead.
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { configurations } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${configurations.version}/configurations${endpoint}`;
  }

  /**
   * Retrieves configurations for a given namespace.
   *
   * @deprecated since in Express, used in legacy projects - Use Configuration.getConfigurations instead.
   * @param {string} namespace - The namespace for which to retrieve configurations.
   * @returns {Promise} A promise that resolves with the configurations.
   */
  static async getConfigurations(namespace) {
    const url = Configurations.createServerUrl(`/Ui/?label=master&profile=prod&fields=${namespace}`);

    return axiosInstance.get(url, { disallowHeaders: ['Authorization'] });
  }

  /**
   * Retrieves all configurations.
   *
   * @deprecated since in Express, used in legacy projects - Use Configuration.getAllConfigurations instead.
   * @returns {Promise} A promise that resolves with all configurations.
   */
  static async getAllConfigurations() {
    const url = Configurations.createServerUrl(`/Ui/?label=master&profile=prod`);

    return axiosInstance.get(url, { disallowHeaders: ['Authorization'] });
  }
}

/**
 * Handles the creation and retrieval of application configurations from a centralized service.
 */
export class Configuration {
  /**
   * Constructs a URL for accessing the Configuration service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { configurations } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${configurations.version}/configuration${endpoint}`;
  }

  /**
   * Retrieves all configurations for the application.
   *
   * @returns {Promise} A promise that resolves with the configurations retrieved from the server.
   */
  static async getAllConfigurations() {
    const url = Configuration.createServerUrl(`/external/Ui`);

    return axiosInstance.get(url, {
      tokenScope: 'appToken',
      params: { profile: `prod`, label: `master` },
    });
  }

  /**
   * Retrieves configurations specific to the backoffice application.
   *
   * @returns {Promise} A promise that resolves with the backoffice configurations retrieved from the server.
   */
  static async getBackofficeConfigurations() {
    const url = Configuration.createServerUrl(`/external/backoffice/UiBackoffice`);

    return axiosInstance.get(url, {
      tokenScope: 'appToken',
      params: { profile: `prod`, label: `master` },
    });
  }

  /**
   * Retrieves configurations specific to the user portal application.
   *
   * @returns {Promise} A promise that resolves with the user portal configurations retrieved from the server.
   */
  static async getUserPortalConfigurations() {
    const url = Configuration.createServerUrl(`/external/user-portal/UiUserPortal`);
    return axiosInstance.get(url, {
      tokenScope: 'appToken',
      params: { profile: `prod`, label: `master` },
    });
  }
}

/**
 * @deprecated in express - This class is deprecated and will be removed in future versions. Use the `IdentityVerification` class instead.
 */
export class Selfie {
  /**
   * Constructs a URL for accessing the Selfie service endpoints.
   *
   * @deprecated in express - Use `IdentityVerification.createServerUrl` instead.
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { selfie } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${selfie.version}/selfie${endpoint}`;
  }

  /**
   * Verifies an applicant's selfie.
   *
   * @deprecated in express
   * @param {Object} data - The data to be sent for verification.
   * @returns {Promise} A promise that resolves with the verification result.
   */
  static async verifyApplicant(data) {
    const url = Selfie.createServerUrl(`/verify-applicant`);

    return axiosInstance.post(url, data);
  }

  /**
   * Verifies a guest's selfie.
   *
   * @deprecated in express
   * @param {Object} data - The data to be sent for verification.
   * @returns {Promise} A promise that resolves with the verification result.
   */
  static async verifyGuest(data) {
    const url = Selfie.createServerUrl(`/verify-guest`);

    return axiosInstance.post(url, data);
  }

  /**
   * Retrieves configuration for the selfie check.
   *
   * @deprecated in express
   * @returns {Promise} A promise that resolves with the configuration data.
   */
  static async getConfigSelfieCheck() {
    const url = Selfie.createServerUrl(`/getconfig-selfie-check`);

    return axiosInstance.get(url);
  }
}

/**
 * Provides methods for identity verification processes.
 */
export class IdentityVerification {
  /**
   * Constructs a URL for accessing the Identity Verification service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { identityVerification } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${identityVerification.version}/selfie${endpoint}`;
  }

  /**
   * Retrieves the URL for an iframe used in identity verification for a specific case.
   *
   * @param {string} caseUuid - The UUID of the case for which to retrieve the iframe URL.
   * @param {boolean} [isWebView=false] - Specifies if the iframe is to be used within a WebView. This can alter the URL parameters to optimize for WebView usage.
   * @param {string} [vendor] - Optional vendor parameter to specify which IDV provider to use (au10tix, jumio, scanovate).
   * @param {string} [redirectParams] - Optional redirectParams to be used for a provider's redirect URL (for now Scanovate).
   * @returns {Promise} A promise that resolves with the iframe URL. The promise can reject if there are issues with the network request.
   */
  static async getIframeUrl(caseUuid, isWebView = false, vendor = undefined, redirectParams = undefined) {
    const url = IdentityVerification.createServerUrl(`/idv-verify/case/${caseUuid}`);
    const params = { isWebView };

    if (vendor) {
      params.vendor = vendor;
    }

    if (redirectParams) {
      params.redirectParams = redirectParams;
    }

    return axiosInstance.get(url, { params });
  }

  /**
   * Builds the IDV status SSE stream URL for a case. `refId` is the current attempt's ref (from
   * `getIframeUrl`); the BE completes the stream when the case reflects it, so it must be sent.
   */
  static createStatusStreamUrl(caseUuid, refId) {
    const url = IdentityVerification.createServerUrl(`/idv/stream/${caseUuid}`);
    return refId ? `${url}?refId=${encodeURIComponent(refId)}` : url;
  }

  /**
   * Auth init for the IDV status stream, mirroring the axios request interceptor so the
   * stream authenticates identically (cookie + CSRF, or bearer). Kept here so auth keeps a
   * single source of truth.
   */
  static #buildIdvStreamInit() {
    const headers = { Accept: 'text/event-stream' };
    let credentials = 'same-origin';

    if (Tools.getAuthConfig('withCredentials')) {
      credentials = 'include';
      const csrf = Tools.getOption('csrfToken');
      if (csrf) headers['X-CSRF-Token'] = csrf;
      const productPath = Tools.getAuthConfig('productPath');
      if (productPath) headers['X-Product-Path'] = productPath;
    } else {
      const tokenType = Tools.getAuthConfig('tokenType');
      const token = Tools.options.accessToken || Tools.options.caseToken || Tools.options.appToken;
      headers.Authorization = `${tokenType} ${token}`;
    }

    return { headers, credentials };
  }

  /**
   * Opens an SSE stream of the IDV status for a case (DCJ-9443). Invokes
   * `onStatus({ caseUuid, status })` for each pushed event, and `onError` on failure or when
   * the server closes the stream without an abort (the caller should fall back to polling).
   * Returns a function that closes the stream.
   *
   * Uses `fetch` + `ReadableStream` because the browser axios adapter (XHR) cannot stream a
   * response body. Auth comes from the same `Tools` config the axios interceptor uses, so it
   * works in both cookie and bearer modes.
   *
   * @param {string} caseUuid
   * @param {{ onStatus?: function, onError?: function, refId?: string }} handlers - `refId` is the
   *   current attempt's ref (from `getIframeUrl`); the BE finishes the stream when the case
   *   reflects it. Without it the BE returns 400 and the caller falls back to polling.
   */
  static openIdvStatusStream(caseUuid, { onStatus, onError, refId } = {}) {
    const controller = new AbortController();
    const url = IdentityVerification.createStatusStreamUrl(caseUuid, refId);
    const { headers, credentials } = IdentityVerification.#buildIdvStreamInit();

    const run = async () => {
      const response = await fetch(url, { method: 'GET', headers, credentials, signal: controller.signal });
      if (!response.ok || !response.body) {
        // Includes the 503 kill-switch -> caller falls back to polling.
        throw new Error(`IDV status stream failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let separator;
        while ((separator = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, separator);
          buffer = buffer.slice(separator + 2);
          const data = frame
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trim())
            .join('\n');
          if (data && typeof onStatus === 'function') {
            onStatus(JSON.parse(data));
          }
        }
      }
    };

    run()
      .then(() => {
        // Server closed the stream without an abort -> let the caller decide (fall back).
        if (typeof onError === 'function') onError(new Error('IDV status stream closed'));
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && typeof onError === 'function') onError(error);
      });

    return () => controller.abort();
  }
}

/**
 * @deprecated in expess - This class and its methods are deprecated.
 */
export class CrossChannel {
  /**
   * Constructs a URL for accessing the CrossChannel service endpoints.
   *
   * @deprecated in expess
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { sendShortURL } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${sendShortURL.version}/cross-channel${endpoint}`;
  }

  /**
   * Sends a short URL to a recipient as part of a cross-channel communication strategy.
   *
   * @deprecated in expess
   * @param {string} [caseId=Tools.getOption('caseId')] - The case ID associated with the short URL.
   * @param {string} redirectUrl - The URL to which the recipient will be redirected.
   * @param {string} [redirectPolicy='id-verification-sms'] - The policy determining how redirects are handled.
   * @param {string} recipient - The recipient of the short URL.
   * @returns {Promise} A promise that resolves with the result of the send operation.
   */
  static async sendShortUrl(
    caseId = Tools.getOption('caseId'),
    redirectUrl,
    redirectPolicy = 'id-verification-sms',
    recipient,
  ) {
    const url = CrossChannel.createServerUrl(`/sendShortUrl`);
    const data = { redirectUrl, redirectPolicy, recipient };

    return axiosInstance.post(url, data, {
      params: {
        caseUuid: caseId,
      },
    });
  }
}

/**
 * Provides methods related to signature processing within the application.
 */
export class Signature {
  /**
   * Constructs a URL for accessing the Signature service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { signature } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${signature.version}/signature${endpoint}`;
  }

  /**
   * Retrieves the links associated with a specific case UUID for signature processing.
   *
   * @param {string} caseUuid - The UUID of the case for which to retrieve signature links.
   * @returns {Promise} A promise that resolves with the signature links for the specified case.
   */
  static getLinks(caseUuid) {
    const url = Signature.createServerUrl(`/cases/${caseUuid}/links`);

    return axiosInstance.get(url);
  }
}

/**
 * Provides methods for VAT (Value Added Tax) related operations.
 */
export class Vat {
  // TODO: Move to Case class, and refactor all consumers to use it from the Case class.

  /**
   * Constructs a URL for accessing the VAT service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { vat } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${vat.version}${endpoint}`;
  }

  /**
   * Generates a login URL for a linked service account within a case.
   * Note: This method is currently misplaced and should be moved to a more appropriate class related to case management.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} linkedService - The service linked to the case.
   * @param {string} vrn - The VAT Registration Number associated with the account.
   * @returns {Promise} A promise that resolves with the generated login URL.
   */
  static getLink(caseUuid, linkedService, vrn) {
    const url = Vat.createServerUrl(`/case-manager/cases/${caseUuid}/accounts/${linkedService}/generate-login-url`);

    return axiosInstance.post(url, { accountType: vrn });
  }

  /**
   * Validates a VAT Registration Number (VRN) for a specific case.
   *
   * @param {string} caseUuid - The UUID of the case for which the VRN validation is requested.
   * @param {string} vrn - The VAT Registration Number to validate.
   * @returns {Promise} A promise that resolves with the validation result.
   */
  static validateVrn(caseUuid, vrn) {
    const url = Vat.createServerUrl(`/vat/validate-vrn/${caseUuid}`);

    return axiosInstance.get(url, { params: { vrn } });
  }
}

/**
 * Represents the decision-making capabilities within the SDK.
 */
export class Decision {
  /**
   * Constructs a URL for accessing the Decision service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { decision } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${decision.version}/decision-components/cases${endpoint}`;
  }

  /**
   * Calculates the offer for a loan based on the provided case UUID, loan amount, and loan term.
   *
   * @param {string} caseUuid - The UUID of the case for which the offer calculation is requested.
   * @param {number} loanAmount - The amount of the loan for which the offer is being calculated.
   * @param {number} loanTerm - The term of the loan in months.
   * @param {string} [policy='INDICATIVE_PRICING_OFFER##!!@@expresslimitedaffordability'] - The policy under which the offer calculation is performed.
   * @returns {Promise} A promise that resolves with the calculated offer details.
   */
  static calculateOffer(
    caseUuid,
    loanAmount,
    loanTerm,
    policy = 'INDICATIVE_PRICING_OFFER##!!@@expresslimitedaffordability',
  ) {
    const url = Decision.createServerUrl(`/${caseUuid}/decision/calculate`);

    return axiosInstance.post(url, { loanAmount, loanTerm }, { params: { policy } });
  }
}

/**
 * Provides methods for interacting with extension services, including retrieving extension data and rejection rules.
 */
export class Extensions {
  /**
   * Constructs a URL for accessing the Extensions service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { extensions } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${extensions.version}/extension-data${endpoint}`;
  }

  /**
   * Retrieves rejection rules for a specific case.
   *
   * @param {string} caseUuid - The UUID of the case for which to retrieve rejection rules.
   * @param {Object} params - Additional parameters to pass to the request, such as filters and pagination options.
   * @returns {Promise} A promise that resolves with the rejection rules for the specified case.
   */
  static async getRejectionRules(caseUuid, params) {
    const url = Extensions.createServerUrl(`/rules-messages/case/${caseUuid}`);

    return axiosInstance.get(url, { params });
  }
}

/**
 * Represents the Backoffice operations, providing methods to interact with back-office related services.
 */
export class Backoffice {
  /**
   * Creates a URL for accessing the Backoffice service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @param {boolean} [includesProduct=true] - Whether to include the product parameter in the URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, version, and product specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '', includesProduct = true) {
    const host = Tools.options.host;
    const { backoffice } = Tools.options.services;

    let url = `${Tools.options.protocol}://${host}/${backoffice.version}/back-office${endpoint}`;

    if (includesProduct) {
      url += `?product=${Tools.options.product}`;
    }

    return url;
  }

  /**
   *  Retrieves the list of products for the tenant.
   *
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async getProducts() {
    const url = Backoffice.createServerUrl(`/manager/products`, false);

    return axiosInstance.get(url);
  }

  /**
   *  Retrieves the product based on the case UUID
   *
   * @param {String} caseIUuid
   * @returns {Promise} A promise that resolves with the server response.
   */
  static async getProductByCase(caseUuid) {
    const url = Backoffice.createServerUrl(`/underwriter/cases/product-by-case/${caseUuid}`, false);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves loans based on provided data and parameters. Supports request cancellation.
   *
   * @param {Object} data - The data to be sent as the request body.
   * @param {Object} params - The parameters to be sent with the request.
   * @param {AbortSignal} signal - The signal to cancel the request.
   * @returns {Promise<Object>} A promise that resolves with the loans data.
   */
  // TODO: Handle request cancellation in a common place.
  static async getLoans(data, params, signal) {
    const url = Backoffice.createServerUrl(`/lms/loans`, false);

    try {
      return await axiosInstance.post(url, data, { params, signal });
    } catch (e) {
      if (!(e instanceof CanceledError)) throw e;
    }
  }

  /**
   * Retrieves the count of loans.
   *
   * @returns {Promise<Object>} A promise that resolves with the loans count data.
   */
  static getLoansCount = () => {
    const url = Backoffice.createServerUrl(`/lms/loans/counts`, false);

    return axiosInstance.get(url);
  };

  /**
   * Pushes forwards the loan with the passed date and retrieve new calculations on the loan details.
   *
   * @param {number} refNum - The reference number of the loan.
   * @param {{firstNewInstalmentDate: string }} data  - The date to which the laon to be pushed forward.
   * @param {{ product: string, performSave: boolean, now: string }} params - The product of the loan &
   *          the flag which indicates if the loan will be initated or not (false is used for informative calculation) and a DateTime formatted string.
   *
   * @returns {Promise<Object>} A promise that resolves with the calculations of the loan details.
   */
  static pushForwardLoan = (refNum, data, params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/defer-loan-schedule?${queryString}`, false);
    return axiosInstance.post(url, data);
  };

  /**
   * Splits the loan into number of payments and retrieve new calculations on the loan details.
   *
   * @param {number} refNum - The reference number of the loan.
   * @param {{newInstalmentDay: number, scheduleLength: number,
   *          repaymentPeriodKind: string, customPeriodLength: number }} data  - The data to be sent as the request body.
   * @param {{ product: string, performSave: boolean, now: string }} params - The product of the loan
   *          & the flag which indicates if the loan will be initated or not (false is used for informative calculation) and a DateTime formatted string.
   *
   * @returns {Promise<Object>} A promise that resolves with the calculations of the loan details.
   */
  static splitLoan = (refNum, data, params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/defer-loan-schedule-by-day?${queryString}`, false);
    return axiosInstance.post(url, data);
  };

  /**
   * Retrieves cases based on provided data and parameters.
   *
   * @param {Object} data - The data to be sent as the request body.
   * @param {Object} params - The parameters to be sent with the request.
   * @returns {Promise<Object>} A promise that resolves with the cases data.
   */
  static async getCases(data, params) {
    const url = Backoffice.createServerUrl(`/underwriter/cases/application-cases`, false);

    return axiosInstance.post(url, data, { params });
  }

  /**
   * Retrieves the count of cases.
   *
   * @returns {Promise<Object>} A promise that resolves with the cases count data.
   */
  static getCasesCount = () => {
    const url = Backoffice.createServerUrl(`/cases/counts`);

    return axiosInstance.get(url);
  };

  /**
   * Retrieves detailed information about a specific case by its reference number.
   *
   * @param {string} refNum - The reference number of the case.
   * @returns {Promise<Object>} A promise that resolves with the case details.
   */
  static async getCaseDetails(refNum) {
    const url = Backoffice.createServerUrl(`/cases/${refNum}`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves case details using a loan reference number.
   *
   * @param {string} refNum - The loan reference number associated with the case.
   * @returns {Promise<Object>} A promise that resolves with the case details.
   */
  static async getCaseDetailsByLoanRef(refNum) {
    const url = Backoffice.createServerUrl(`/cases/loan-reference-number/${refNum}`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves loans associated with a specific case reference number.
   *
   * @param {string} refNum - The reference number of the case.
   * @returns {Promise<Object>} A promise that resolves with the loans data.
   */
  static async getLoansByCase(refNum) {
    const url = Backoffice.createServerUrl(`/cases/get-loans-by-case/${refNum}`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the state of a loan by its reference number.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<Object>} A promise that resolves with the loan state data.
   */
  static getLoanState = (refNum, params) => {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/state`);

    return axiosInstance.get(url, { params });
  };

  /**
   * Retrieves the instalments for a loan by its reference number.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<Object>} A promise that resolves with the instalments data.
   */
  static getInstalments = (refNum, params) => {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/instalments-overview`);

    return axiosInstance.get(url, { params });
  };

  /**
   * Retrieves the transactions for a loan by its reference number.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<Object>} A promise that resolves with the transactions data.
   */
  static getTransactions = (refNum, params) => {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/transactions-overview`);

    return axiosInstance.get(url, { params });
  };

  /**
   * Retrieves the repayment history for a loan by its reference number.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<Object>} A promise that resolves with the repayment history data.
   */
  static getRepaymentHistory = (refNum, params) => {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/repayment-history`);

    return axiosInstance.get(url, { params });
  };

  /**
   * Retrieves a signed document by its document ID.
   *
   * @param {string} docID - The ID of the document.
   * @returns {Promise<Object>} A promise that resolves with the signed document data, returned as an array buffer.
   */
  static async getSignedDocument(docID) {
    const url = Backoffice.createServerUrl(`/documents/${docID}`);

    return axiosInstance.get(url, { responseType: 'arraybuffer' });
  }

  /**
   * Makes a full/partial repayment.
   * Also used for making an calculation for a repayment (via performSave=false)
   *
   * @param {number} refNum - The loan reference number.
   * @param {Boolean} performSave - Indicates whether a calculation should be performed or an actual repayment.
   * @param {Object} paymentData - The data for the repayment creation. It includes:
   *   @param {number} paymentData.amount - The amount of the repayment.
   *   @param {number} paymentData.kind - The kind of the repayment (full/partial/other...).
   * @returns {Promise<Object>} A promise that resolves with the response upon successful repayment.
   */
  static async processRepayment(refNum, paymentData, performSave = true) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/process-repayment`);

    return axiosInstance.post(url, paymentData, { params: { performSave } });
  }

  /**
   * Manually creates a new repayment for a loan in the backoffice.
   * Also used for making an actual repayment.
   *
   * @param {number} refNum - The loan reference number.
   * @param {Object} paymentData - The data for the repayment creation. It includes:
   *   @param {number} paymentData.amount - The amount of the repayment.
   *   @param {Date} paymentData.time - The date and time of the repayment.
   *   @param {string} paymentData.description - A description of the repayment.
   *   @param {string} paymentData.method - The method of payment, e.g., "BankTransfer".
   *   @param {string} paymentData.ip - The IP address from which the repayment is made.
   * @returns {Promise<Object>} A promise that resolves with the response from the backoffice upon successful repayment creation.
   */
  static async createRepayment(refNum, paymentData) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/repayment`);

    return axiosInstance.post(url, paymentData);
  }

  /**
   * Manually enables auto repayments for a loan.
   *
   * @param {number} refNum - The loan reference number.
   * @param {Object} data - The data for enabling auto repayments. It includes:
   *   @param {string} data.notes - Notes or comments about enabling auto repayments.
   * @returns {Promise<Object>} A promise that resolves with the response from the backoffice upon successful operation.
   */
  static async unfreezeAutoRepayment(refNum, data) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/start-auto-repayment`);

    return axiosInstance.put(url, data);
  }

  /**
   * Manually disables auto repayments for a loan.
   *
   * @param {number} refNum - The loan reference number.
   * @param {Object} data - The data for disabling auto repayments. It includes:
   *   @param {string} data.notes - Notes or comments about disabling auto repayments.
   *   @param {string} data.startDate - The start date from which auto repayments should be stopped, in "YYYY-MM-DD" format.
   *   @param {number} data.reasonID - The ID of the reason for stopping auto repayments.
   * @returns {Promise<Object>} A promise that resolves with the response from the backoffice upon successful operation.
   */
  static async freezeAutoRepayment(refNum, data) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/stop-auto-repayment`);

    return axiosInstance.put(url, data);
  }

  /**
   * Updates the status of a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @param {Object} data - The data containing the new status information.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful status update.
   */
  static async updateCaseStatus(caseUuid, data) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/update-status`);

    return axiosInstance.put(url, data);
  }

  /**
   * Updates the decision for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @param {Object} data - The data containing the new decision information.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful decision update.
   */
  static async updateDecision(caseUuid, data) {
    const url = Backoffice.createServerUrl(`/underwriter/cases/${caseUuid}/update-decision`);

    return axiosInstance.post(url, data);
  }

  /**
   * Retrieves the default reasons for making a loan default.
   *
   * @returns {Promise<Object>} A promise that resolves with the list of default reasons from the server.
   */
  static async getDefaultReasons() {
    const url = Backoffice.createServerUrl(`/lms/make-default/list-reasons`, false);

    return axiosInstance.get(url);
  }

  /**
   * Marks a loan as default.
   *
   * @param {number} refNum - The reference number of the loan.
   * @param {Object} data - The data containing the reason and details for making the loan default.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful operation.
   */
  static async makeDefault(refNum, data) {
    const url = Backoffice.createServerUrl(`/lms/make-default/${refNum}`);

    return axiosInstance.post(url, data);
  }

  /**
   * Adds a refund to a loan.
   *
   * @param {number} refNum - The reference number of the loan.
   * @param {Object} data - The data containing the refund details.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful refund addition.
   */
  static async addRefund(refNum, data) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/add-refund`);

    return axiosInstance.post(url, data);
  }

  /**
   * Retrieves extended OCR details for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @param {string} [fileType='ESNA'] - The type of file to retrieve OCR details for. Defaults to 'ESNA'.
   * @returns {Promise<Object>} A promise that resolves with the extended OCR details.
   */
  static async getOcrDetailsExtended(caseUuid, fileType = 'ESNA') {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/ocr-details-extended`);

    return axiosInstance.get(url, { params: { fileType } });
  }

  /**
   * Retrieves OCR details for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the OCR details.
   */
  static async getOCRDetails(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/ocr-details`);

    return axiosInstance.get(url);
  }

  /**
   * Updates OCR details for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @param {Object} data - The data containing the updated OCR details.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful update.
   */
  static async updateOCRDetails(caseUuid, data) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/update-ocr`);

    return axiosInstance.patch(url, data);
  }

  /**
   * Retrieves the OCR history for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the OCR history.
   */
  static async getOCRHistory(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/history-table`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves OCR rule details for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @param {string} [primaryFileType='OCR'] - The primary file type for which to retrieve rule details. Defaults to 'OCR'.
   * @returns {Promise<Object>} A promise that resolves with the OCR rule details.
   */
  static async getOCRRuleDetails(caseUuid, primaryFileType = 'OCR') {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/rule-details`);

    return axiosInstance.get(url, { params: { primaryFileType } });
  }

  /**
   * Retrieves the outcome of the ruleset evaluation for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the ruleset outcome information.
   */
  static async getRulesetOutcome(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/outcome-ruleset-info`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the audit log of the ruleset evaluation for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the ruleset audit log.
   */
  static async getRulesetAudit(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/ruleset-audit`);

    return axiosInstance.get(url);
  }

  /**
   * Progresses the validation state of a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the server's response upon successfully progressing the case's validation state.
   */
  static async progress(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/progress-validation`);

    return axiosInstance.post(url);
  }

  /**
   * Initiates the validation process for a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the server's response upon successfully running the validation.
   */
  static async runValidation(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/run-validation`);

    return axiosInstance.post(url);
  }

  /**
   * Closes a specific case.
   *
   * @param {string} caseUuid - The unique identifier of the case.
   * @returns {Promise<Object>} A promise that resolves with the server's response upon successfully closing the case.
   */
  static async closeCase(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/close`);

    return axiosInstance.patch(url);
  }

  /**
   *  Retrieves the list of all whitelisted numbers.
   *
   * @returns {Promise<Object>} A promise that resolves with an array of all whitelisted numbers objects.
   */
  static async getAllWhitelistedNumbers() {
    const url = Backoffice.createServerUrl(`/mobile-phone-white-list`, false);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the whitelisted number by its phone number.
   *
   * @param {*} phoneNumber - The phone number to retrieve.
   * @returns {Promise<Object>} A promise that resolves with the whitelisted number object.
   */
  static async getWhitelistedNumberByNumber(phoneNumber) {
    const url = Backoffice.createServerUrl(`/mobile-phone-white-list/${phoneNumber}`, false);

    return axiosInstance.get(url);
  }

  /**
   *  Adds a new whitelisted number.
   *
   * @param {*} phoneNumber - The phone number to add.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful addition.
   */
  static async addWhitelistedNumber({ phoneNumber }) {
    const url = Backoffice.createServerUrl(`/mobile-phone-white-list`, false);

    return axiosInstance.post(url, { phoneNumber });
  }

  /**
   * Removes a whitelisted number.
   * @param {*} phoneNumber - The phone number to remove.
   * @returns {Promise<Object>} A promise that resolves with the response from the server upon successful removal.
   */
  static async removeWhitelistedNumber(phoneNumber) {
    const url = Backoffice.createServerUrl(`/mobile-phone-white-list/${phoneNumber}`, false);

    return axiosInstance.delete(url);
  }

  /**
   * Create a repayment for loan.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<object>} A promise that resolves with the response from the backoffice upon successful repayment creation.
   */
  static async createManualRepayment(refnum, params) {
    const url = Backoffice.createServerUrl(`/maintenance/loans/${refnum}/manual-repayment`);

    return axiosInstance.post(url, null, { params });
  }

  /**
   * Get white list by type.
   *
   * @param {string} type - The type to search for in whitelist ("ID"/"EMAIL").
   * @returns {Promise} A promise that resolves with an array of all whitelisted IDs or emails.
   */
  static async getWhitelistByType(type) {
    const url = Backoffice.createServerUrl(`/maintenance/users/${type}`, false);

    return axiosInstance.get(url);
  }

  /**
   * Get user data by type.
   *
   * @param {string} type - The type to search for in whitelist ("ID"/"EMAIL").
   * @param {string} userIdentification - The input to search by.
   * @returns {Promise} A promise that resolves with the user data.
   */
  static async getUserDataByType(type, userIdentification) {
    const url = Backoffice.createServerUrl(`/maintenance/users/${type}/${userIdentification}`, false);

    return axiosInstance.get(url);
  }

  /**
   * Get reset history.
   *
   * @param {object} params - Additional parameters for the request.
   * @returns {Promise} A promise that resolves with reset history.
   */
  static async getResetHistory(params) {
    const url = Backoffice.createServerUrl(`/maintenance/users/reset-history`, false);

    return axiosInstance.get(url, { params });
  }

  /**
   * Reset a user.
   *
   * @param {string} comment - The comment to add to the reset.
   * @param {Object} params - The parameters to use for resetting a user.
   * @returns {Promise<object>} A promise that resolves with the response from the backoffice upon successful user reset.
   */
  static async resetUser(comment, params) {
    const url = Backoffice.createServerUrl(`/maintenance/reset-user`, false);

    return axiosInstance.post(url, { comment }, { params });
  }

  /**
   * Cancel a loan.
   *
   * @param {string} refNum - The reference number of the loan.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise<object>} A promise that resolves with the response from the backoffice upon successful loan cancellation.
   */
  static async cancelLoan(refnum, data) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refnum}`);

    return axiosInstance.post(url, data);
  }

  static async updateMarker(caseId, loanRef, marker) {
    const url = Backoffice.createServerUrl(`/person/${caseId}/marker`);

    return axiosInstance.post(url, { markerName: marker, loanReferenceNumber: loanRef });
  }

  static async reportLoanStatus(refNum, checked) {
    const url = Backoffice.createServerUrl(`/cases/loans/${refNum}/update-loan-reporting-status`);

    return axiosInstance.patch(url, { reportingStatus: checked });
  }

  static async getCaseJourneys(caseUuid) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/journeys`);

    return axiosInstance.get(url);
  }

  static async updateCaseJourney(caseUuid, journeyUuid, { stepName, uiStep }) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/journey/${journeyUuid}`);

    return axiosInstance.patch(url, { stepName, uiStep });
  }

  static async freezePayment(refNum, performSave = false) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/freeze-payment`) + `&performSave=${performSave}`;

    return axiosInstance.post(url);
  }

  /**
   * Sends repayment documents for a loan.
   *
   * @param {Array<string>} data - The document uuids to be sent.
   * @returns {Promise<Object>} A promise that resolves with the response from the backoffice upon successful operation.
   */
  static async sendRepaymentDocuments(data) {
    const url = Backoffice.createServerUrl(`/lms/loans/send-repayment-documents`);

    return axiosInstance.post(url, data);
  }

  /**
   * Reschedule loan with new date and retrieve new calculations on the loan details.
   *
   * @param {number} refNum - The reference number of the loan.
   * @param {{firstNewInstalmentDate: string }} data  - The date to which the loan to be rescheduled.
   * @param {{ product: string, performSave: boolean, loanEndDate: string }} params - The product of the loan &
   *          the flag which indicates if the loan will be initiated or not (false is used for informative calculation) and a DateTime formatted string for the new loan end date.
   *
   * @returns {Promise<Object>} A promise that resolves with the calculations of the loan details.
   */
  static async loanReschedule(refNum, data, params) {
    const queryString = new URLSearchParams(params).toString();
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/loan-reschedule?${queryString}`, false);

    return axiosInstance.post(url, data);
  }

  /**
   * Sets the court case open date for a specific case.
   *
   * @param {string} caseUuid - The UUID of the case.
   * @param {string} courtCaseOpenDateStr - The court case open date as a string.
   * @returns {Promise<Object>} A promise that resolves with the server response.
   */
  static async setCourtCaseOpenDate(caseUuid, courtCaseOpenDateStr) {
    const url = Backoffice.createServerUrl(`/cases/${caseUuid}/set-court-case-open-date`);

    return axiosInstance.patch(url, { courtCaseOpenDateStr });
  }

  /**
   * Updates the debt classification for a loan.
   *
   * @param {string} refNum - The loan reference number.
   * @param {string} product - The product identifier for multi-tenant context.
   * @param {Object} body - The request body: currentClassification, updatedClassification, type (e.g. "manual"), lateDays.
   * @returns {Promise<Object>} A promise that resolves with the response from the server.
   */
  static async updateDebtClassification(refNum, product, body) {
    const url = Backoffice.createServerUrl(`/lms/loans/${refNum}/debt-classification`, false);

    return axiosInstance.patch(url, body, { params: { product } });
  }

  /**
   * Retrieves the Validit.AI channels configuration.
   *
   * @param {string} tenant - The tenant identifier.
   * @returns {Promise<Object>} A promise that resolves with the channels configuration.
   */
  static async getValiditAiChannelsConfig(tenant, product) {
    let url = Backoffice.createServerUrl('/validit/ai-channels-config', false) + `?tenant=${tenant}`;
    if (product) {
      url += `&product=${product}`;
    }

    return axiosInstance.get(url);
  }

  /**
   * Saves the Validit.AI channels configuration.
   *
   * @param {string} tenant - The tenant identifier.
   * @param {Object} payload - The configuration payload with channels and killSwitchEnabled.
   * @returns {Promise<Object>} A promise that resolves with the server response.
   */
  static async saveValiditAiChannelsConfig(tenant, payload, product) {
    let url = Backoffice.createServerUrl('/validit/ai-channels-config', false) + `?tenant=${tenant}`;
    if (product) {
      url += `&product=${product}`;
    }

    return axiosInstance.post(url, payload);
  }
}

/**
 * Represents the CME Loan operations within the SDK, providing methods to interact with the CME Loan API.
 */
export class CMELoan {
  /**
   * Creates a URL for the CME Loan API.
   *
   * @param {string} endpoint - The endpoint to append to the base URL.
   * @returns {string} The fully-qualified URL.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { cmeLoan } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${cmeLoan.version}/cme-loan${endpoint}`;
  }

  /**
   * Retrieves loans associated with a specific case UUID.
   *
   * @param {string} caseUuid - The UUID of the case for which to retrieve loans.
   * @returns {Promise<object>} A promise that resolves with the loans data for the specified case.
   */
  static async getLoansByCaseId(caseUuid) {
    const url = CMELoan.createServerUrl(`/dashboard/get-loans-by-case/${caseUuid}`);

    return axiosInstance.get(url);
  }

  /**
   * Retrieves the state of a loan identified by its reference number. Optionally, it can return the loan's future final state.
   *
   * @param {string} refnum - The reference number of the loan.
   * @param {boolean} finalState - If false, retrieves the current state of the loan. If true, retrieves the loan's future final state based on the last repayment.
   * @returns {Promise<object>} A promise that resolves with the loan state data.
   */
  static async getLoanStateByReferenceNumber(refnum, finalState) {
    const url = CMELoan.createServerUrl(`/dashboard/loan-state/${refnum}`);
    const params = { finalState };

    return axiosInstance.get(url, { params });
  }

  static async getLoans(params) {
    const url = CMELoan.createServerUrl(`/dashboard/get-loans`);

    return axiosInstance.get(url, { params });
  }
}

/**
 * Represents the CRM (Customer Relationship Management) operations within the SDK, providing methods to interact with the CRM API.
 */
export class CRM {
  /**
   * Constructs a URL for accessing the CRM service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { crm } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${crm.version}/crm-data${endpoint}`;
  }

  /**
   * Sends data to the CRM system for form submission.
   *
   * @param {Object} data - The data to be sent to the CRM system. This typically includes form fields and values.
   * @returns {Promise} A promise that resolves with the response from the CRM system upon successful form submission.
   */
  static async sendCRMForm(data) {
    const url = CRM.createServerUrl('/create');

    return axiosInstance.post(url, data);
  }
}

export class Greenlend {
  /**
   * Constructs a URL for accessing the Greenlend service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { greenlend } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${greenlend.version}/greenlend${endpoint}`;
  }

  /**
   * Retrieves optional offers based on a provided data.
   *
   * @param {string} caseUuid - The UUID of the case for which data is being retrieved.
   * @param {Object} data - The data to be sent.
   * @returns {Promise} An optional offers.
   */
  static async calculateOffer(caseUuid, data) {
    const url = Greenlend.createServerUrl(`/crm/${caseUuid}/optional-offer`);

    return axiosInstance.post(url, data);
  }

  /**
   * Retrieves a remaining account for the case.
   *
   * @param {string} caseUuid - The UUID of the case for which data is being retrieved.
   * @returns {Promise} A case remaining amount.
   */
  static async getRemainingAmount(caseUuid) {
    const url = Greenlend.createServerUrl(`/crm/${caseUuid}/remaining-amount`);

    return axiosInstance.get(url);
  }
}

/**
 * Represents the Admin Agent operations within the SDK, providing methods to interact with the Admin Agent API.
 */
export class AdminAgent {
  /**
   * Creates a URL for accessing the AdminAgent service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @param {boolean} [includesProduct=true] - Whether to include the product parameter in the URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, version, and product specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '', initialParams = {}) {
    const { host, protocol, services, product, channel } = Tools.options;
    const { backoffice } = services;

    const params = { ...initialParams, product };

    if (channel) params.channel = channel;

    const queryString = new URLSearchParams(params).toString();

    return `${protocol}://${host}/${backoffice.version}/admin-agent/configurations/scopes${endpoint}?${queryString}`;
  }

  /**
   * Retrieves the data from the admin agent for a scope
   * @param {string} scope - The scope for which to retrieve data.
   * @param {Object} params - Additional parameters for the request.
   * @returns {Promise} A promise that resolves with the data retrieved from the server.
   */
  static async getData(scope, params) {
    const url = AdminAgent.createServerUrl(`/${scope}`, params);

    return axiosInstance.get(url);
  }

  /**
   * Apply the data to the admin agent for a scope
   * @param {String} scope - The scope for which to apply data.
   * @param {Object} params - Additional parameters for the request.
   * @param {Object} data - The data to apply.
   * @returns {Promise} A promise that resolves with the data applied to the server.
   */
  static async postData(scope, params, data) {
    const url = AdminAgent.createServerUrl(`/${scope}/apply`, params);

    return axiosInstance.post(url, data);
  }
}

export class System {
  static createServerUrl(endpoint = '') {
    const { host, protocol, services } = Tools.options;
    const { system } = services;

    return `${protocol}://${host}/${system.version}/system${endpoint}`;
  }

  static getMaintenanceData() {
    const url = System.createServerUrl('/maintenance/data');

    return axiosInstance.get(url);
  }

  static getTokenData() {
    const url = System.createServerUrl('/cookie/data');

    return axiosInstance.get(url);
  }
}

export class Channels {
  static createServerUrl(endpoint = '') {
    const { host, protocol, services } = Tools.options;
    const { system } = services;

    return `${protocol}://${host}/${system.version}/channels/case-manager/cases-ott${endpoint}`;
  }

  /**
   * Consumes one time token and switches it for an access token.
   *
   * @param {Object} OTTData - The one time token data.
   * @param {Object} config - Optional axios config (e.g., { signal: abortController.signal }).
   * @param {string} [flow] - Optional flow ('loan' or 'renewal') selecting the consume endpoint variant.
   * @returns {Promise} A promise that resolves with the response from the server containing access token.
   */
  static async consumeOneTimeToken(OTTData, config, flow) {
    const axiosConfig = config ?? {};
    const endpointByFlow = {
      loan: '/consume-loan-flow',
      onboarding_renewal: '/consume-renewal',
    };
    const endpoint = endpointByFlow[flow] || '/consume';
    const url = Channels.createServerUrl(endpoint);

    return axiosInstance.post(url, OTTData, axiosConfig);
  }

  static async clearSession(config) {
    const axiosConfig = config ?? {};
    const url = Channels.createServerUrl('/clear-session');

    // Must go through the shared axiosInstance: its request interceptor attaches
    // X-Product-Path, which the backend uses to derive the per-product cookie names to clear.
    return axiosInstance.post(url, undefined, axiosConfig);
  }
}

/**
 * Provides methods for discount-related operations, including business day calculations.
 */
export class Discount {
  /**
   * Constructs a URL for accessing the Discount service endpoints.
   *
   * @param {string} [endpoint=''] - The specific endpoint to append to the base service URL.
   * @returns {string} The full URL to the specified endpoint, constructed using the host, protocol, and version specified in `Tools.options`.
   */
  static createServerUrl(endpoint = '') {
    const host = Tools.options.host;
    const { discount } = Tools.options.services;

    return `${Tools.options.protocol}://${host}/${discount.version}/discount${endpoint}`;
  }

  /**
   * Retrieves the next business day for a given date.
   *
   * @param {string} dateTime - The date and time in ISO format (e.g., '2025-10-21T00:00:00').
   * @returns {Promise} A promise that resolves with the next business day information.
   */
  static async getNextBusinessDay(dateTime) {
    const url = Discount.createServerUrl(`/next-business-day-il`);

    return axiosInstance.get(url, { params: { dateTime } });
  }

  /**
   * Retrieves legal credit consent statement document for a case.
   *
   * Server response shape:
   * {
   *   documentId: string,
   *   documentContent: string, // base64-encoded PDF bytes
   *   documentSuffix: string,  // e.g. 'PDF'
   *   documentPageCount: number
   * }
   * In case there are no documents for the user, the server returns 400.
   *
   * @param {string} caseUuid - The case UUID.
   * @returns {Promise} A promise resolving to the server response.
   */
  static async getStatementDocuments(caseUuid) {
    const url = Discount.createServerUrl(`/statement-documents/${caseUuid}`);

    return axiosInstance.get(url);
  }
}
